/**
 * Flat reducer for the Agent Panel stream state.
 *
 * One state per session. Each outbound frame from the server is
 * dispatched as an action; the reducer builds up a linear list of
 * turns. Each turn has a stable ``blocks`` map keyed by the Anthropic
 * ``content_block.index`` and progressively filled by
 * ``content_block_start`` / ``content_block_delta`` /
 * ``content_block_stop`` / ``message_delta`` / ``message_stop``
 * events, then superseded by the final ``assistant`` frame that
 * carries the canonical message.
 *
 * The UI renders from ``turns``; tool_result user frames are routed
 * to ``toolResults`` (keyed by ``tool_use_id``) so the renderer can
 * inline them under the matching ToolUseBlock. System notices and
 * subagent task updates have their own side channels.
 */

import type { AgentStarterPromptDisplay } from './starterPrompts';
import { normalizeTaskResultPayload } from './taskResults';
import type {
  AgentSpeed,
  AssistantFrame,
  Citation,
  ContentBlock,
  InteractionRequest,
  InteractionRequestFrame,
  InteractionResponseFrame,
  InteractionStateFrame,
  OutboundFrame,
  PermissionMode,
  RateLimitInfo,
  ResultFrame,
  ServerErrorFrame,
  SessionAttachedFrame,
  SseContentBlockDelta,
  SseContentBlockStart,
  SseMessageDelta,
  SseUsage,
  StopReason,
  SystemApiRetryFrame,
  SystemCompactBoundaryFrame,
  SystemInitFrame,
  TaskNotificationFrame,
  TaskProgressFrame,
  TaskStartedFrame,
  TextBlock,
  ThinkingBlock,
  ToolResultBlock,
  ToolUseBlock,
  UserFrame,
} from './types';

export const DEFAULT_DISPLAY_MODEL = 'claude-opus-4-8';

// =============================================================================
// State shape
// =============================================================================

export type ConnectionStatus = 'idle' | 'connecting' | 'open' | 'closed' | 'error';

/** High-level activity status derived from frame flow. */
export type AgentStatus = 'idle' | 'thinking' | 'streaming' | 'tool_running';

/**
 * Whether a ``task_notification`` status proves the task is no longer
 * running. Monitor match events arrive as *non-terminal* notifications
 * (no terminal status) and must not retire the task — the watch
 * pipeline keeps running after every match.
 */
export function isTerminalTaskStatus(status: string | undefined): boolean {
  return (
    status === 'completed' ||
    status === 'failed' ||
    status === 'stopped' ||
    status === 'killed' ||
    status === 'cancelled' ||
    status === 'canceled'
  );
}

export interface SubagentState {
  taskId: string;
  /** Correlates with the spawning ``Task`` ToolUseBlock id when available. */
  toolUseId?: string | null;
  description: string;
  taskType?: string;
  status: 'running' | 'completed' | 'failed' | 'stopped';
  lastToolName?: string | null;
  outputFile?: string;
  summary?: string;
  usage?: { total_tokens?: number; tool_uses?: number; duration_ms?: number };
}

/**
 * Coarse categorization of an in-flight SDK background task.
 *
 * The Agent SDK ships background work under ``task_started`` /
 * ``task_progress`` / ``task_notification`` regardless of whether
 * the task is a backgrounded bash command, a long-running ``Monitor``
 * watch, a subagent spawned via ``Task`` / ``Agent``, or a remote
 * agent. ``task_type`` alone cannot tell ``Bash`` apart from
 * ``Monitor`` — both ride on ``"local_bash"``. We disambiguate by
 * joining ``task_started.tool_use_id`` to the originating
 * ``ToolUseBlock`` name in :data:`AgentPanelState.toolUseRegistry`,
 * falling back to ``task_type`` when the registry lookup misses.
 */
export type ActiveTaskCategory = 'bash' | 'monitor' | 'agent' | 'unknown';

/**
 * One in-flight SDK background task — i.e. a task we have observed a
 * ``task_started`` for, and not yet a terminal ``task_notification``.
 */
export interface ActiveTask {
  taskId: string;
  toolUseId: string | null;
  /** ``ToolUseBlock.name`` that spawned this task, when known. */
  toolName: string | null;
  taskType?: string;
  description: string;
  category: ActiveTaskCategory;
  lastToolName?: string | null;
  usage?: { total_tokens?: number; tool_uses?: number; duration_ms?: number };
  /** ms epoch when the ``task_started`` was reduced. */
  startedAt: number;
}

export interface AgentTurnWorkState {
  /** ms epoch when the current user turn began, or ``null`` when no turn is live. */
  startedAt: number | null;
  /** Stable completed duration shown after the whole user turn finishes. */
  completedDurationMs: number | null;
}

export interface WorkingIndicatorModel {
  active: boolean;
  durationMs: number | null;
}

export type TurnDisplay = AgentStarterPromptDisplay;

/**
 * Foreground tool currently blocking the assistant turn.
 *
 * This is distinct from :type:`ActiveTask`: foreground Bash/tool calls
 * do not always emit SDK ``task_started`` side-channel frames, so the
 * only reliable source of truth is the pending ``ToolUseBlock`` in the
 * live assistant turn. The compose surface's global interrupt button
 * owns stopping those tools.
 */
export interface ForegroundTool {
  toolUseId: string;
  toolName: string;
  description: string;
  category: ActiveTaskCategory;
  startedAt: number;
}

/** Status values the agent emits for an individual ``TodoWrite`` entry. */
export type TodoStatus = 'pending' | 'in_progress' | 'completed' | 'cancelled';

export interface TodoItem {
  id?: string;
  content: string;
  status: TodoStatus | string;
  activeForm?: string;
  description?: string;
  owner?: string;
  blocks?: string[];
  blockedBy?: string[];
}

/**
 * The agent's current checklist, derived from the latest
 * ``TodoWrite`` tool call. Updated whenever the agent rewrites the
 * list — last write wins, mirroring how Claude Code surfaces the
 * todos in its CLI status pane.
 */
export interface PlanState {
  /** ``todo`` for legacy TodoWrite, ``task`` for TaskCreate/Update/Get/List. */
  kind?: 'todo' | 'task';
  toolUseId: string;
  todos: TodoItem[];
  total: number;
  pending: number;
  inProgress: number;
  completed: number;
  cancelled: number;
  /** First in-progress entry, when one exists. */
  current: TodoItem | null;
  updatedAt: number;
}

/**
 * Derived "what is the agent doing right now" slice.
 *
 * Folded by the reducer from the same SDK frames the rest of the
 * panel renders — ``task_*`` frames feed :attr:`activeTasks`, and
 * ``TodoWrite`` tool input feeds the checklist summary. Designed
 * so a status strip can render counts + todos without re-deriving
 * anything.
 */
export interface ActivityState {
  /** Currently in-flight tasks, removed when their notification arrives. */
  activeTasks: Record<string, ActiveTask>;
  bashCount: number;
  monitorCount: number;
  agentCount: number;
  /** Latest ``TodoWrite`` checklist, or ``null`` until one lands. */
  plan: PlanState | null;
}

/**
 * Per-block timing record captured when ``content_block_start`` and
 * ``content_block_stop`` are reduced. Used by the view to render
 * accurate "Thought for 7s" durations next to thinking blocks while
 * they stream and the moment they freeze. History-loaded blocks
 * never see these events so their entry stays absent — the view
 * falls back to a duration-less label in that case.
 */
export interface BlockTiming {
  startedAt: number;
  endedAt?: number;
}

/** One assistant or user message, or the in-flight shell thereof. */
export interface Turn {
  id: string;
  role: 'assistant' | 'user';
  /** Blocks keyed by SSE ``index``; assembled progressively. */
  blocks: Record<number, ContentBlock>;
  /** Sorted block indices — preserves emission order for rendering. */
  order: number[];
  /** Raw partial JSON strings per block index (for tool_use args). */
  partialToolInput: Record<number, string>;
  /**
   * Per-block start / stop timestamps (ms epoch), keyed by SSE
   * ``index``. Recorded on ``content_block_start`` / ``content_block_stop``
   * so the view can paint a "Thought for Xs" tail next to thinking
   * blocks as they stream and the moment they close.
   */
  blockTimings: Record<number, BlockTiming>;
  model?: string;
  messageId?: string;
  usage?: SseUsage;
  stopReason?: StopReason | string | null;
  /** True once ``assistant`` frame has overlaid authoritative content or role=user echoed. */
  committed: boolean;
  /** When we received the first event for the turn — for sorting/dedup. */
  startedAt: number;
  /**
   * Snapshot of :attr:`AgentPanelState.compactEpoch` taken when this
   * turn was first created. The high-water walk in
   * :func:`_updateContextHighWater` uses this to skip assistant turns
   * whose ``usage`` still reflects a pre-compaction context window —
   * without that filter, a tiny post-compact reply would re-assert
   * the giant pre-compact usage on the very next non-compact frame
   * and the context chip would never drop. Optional so history-replay
   * fixtures and old snapshot frames default to epoch 0 (treated as
   * pre-current-compact) instead of contaminating the chip.
   */
  compactEpoch?: number;
  /** Error from AssistantMessage.error. */
  error?: string | null;
  /**
   * User text this local/server error belongs to. Server-side input
   * rejections can replay from the ring after history hydration; this
   * lets ``prependHistory`` re-anchor the error beside the prompt that
   * caused it instead of leaving it at the transcript tail.
   */
  errorForUserText?: string | null;
  /**
   * True for client-inserted user turns that haven't been reconciled
   * with the server's replayed ``user`` frame yet. Render-identical
   * to a normal user turn — this flag exists purely so ``_onUser`` can
   * find and replace the placeholder in place (preserving its slot in
   * ``turns``) instead of appending a duplicate after the assistant
   * has already started streaming.
   */
  optimistic?: boolean;
  /**
   * ``client_message_id`` from the originating ``user_input`` frame.
   * The server echoes this id back on the SDK's ``user`` frame so the
   * reducer can reconcile by id rather than by text. Absent on user
   * turns that did not originate from a local optimistic send.
   */
  clientMessageId?: string;
  /**
   * True when this user turn should keep the agent waiting for a
   * future assistant turn. Follow-up messages submitted while an
   * assistant/tool turn is already active are folded into that active
   * turn by the SDK, so they must not re-arm completion state.
   */
  awaitsAssistant?: boolean;
  /**
   * Optional UI-only display metadata for turns whose backend payload
   * should stay rich but whose transcript representation should be compact.
   */
  display?: TurnDisplay;
}

export interface SystemNotice {
  id: string;
  type: 'compact_boundary' | 'api_retry' | 'init';
  data: Record<string, unknown>;
  at: number;
  /**
   * Turn id that was at the transcript tail when this notice arrived.
   *
   * The renderer must preserve ``state.turns`` order because history
   * pagination prepends older turns whose ``startedAt`` is reconstructed
   * at load time, not persisted transcript time. Anchoring notices to
   * the current tail lets the UI interleave system rows without sorting
   * the turn list and accidentally moving older pages to the bottom.
   */
  anchorTurnId?: string | null;
}

export interface AgentPanelState {
  attached: boolean;
  cwd: string | null;
  sessionId: string | null;
  model: string | null;
  /** Reasoning effort level reported by the workspace server (e.g. "xhigh"). */
  effort: string | null;
  /** Claude Code speed mode reported by the workspace server. */
  speed: AgentSpeed | string | null;
  /** Runtime permission mode reported by the workspace server. */
  permissionMode: PermissionMode | string | null;
  /** Workspace-agent protocol version. ``null`` means legacy/basic chat only. */
  protocolVersion: number | null;
  /** Server-advertised feature flags for advanced controls. */
  capabilities: string[];

  /** Turns in emission order. */
  turns: Turn[];

  /**
   * Message id for the currently-open SSE assistant stream.
   *
   * Anthropic content block delta events only carry a block index,
   * not the message id. Live reduction therefore has to remember
   * which assistant turn the latest ``message_start`` opened. This
   * also lets reconnect replay ignore deltas for messages that are
   * already committed from history instead of accidentally appending
   * old chunks to the current tail turn.
   */
  activeStreamTurnId: string | null;

  /** Tool result blocks indexed by tool_use_id for inline rendering under the matching ToolUseBlock. */
  toolResults: Record<string, ToolResultBlock>;

  /** Live subagent tasks. */
  subagents: Record<string, SubagentState>;

  /**
   * Map of ``ToolUseBlock.id`` to the originating tool ``name``.
   *
   * Populated as tool-use blocks land (skeletons in stream events,
   * full inputs in ``content_block_stop`` and final ``assistant``
   * frames). The ``activity`` slice consults this when a
   * ``task_started`` arrives so that, e.g. a backgrounded ``Bash``
   * and a long-running ``Monitor`` watch — both tagged
   * ``task_type === 'local_bash'`` by the SDK — can still be split
   * apart in the status surface.
   */
  toolUseRegistry: Record<string, string>;

  /** Derived "what is the agent doing right now" — counts + checklist. */
  activity: ActivityState;

  /** SDK permission/user-input prompts waiting for a webapp response. */
  pendingInteractions: InteractionRequest[];

  rateLimit?: RateLimitInfo;

  /** Latest ResultMessage — carries cost/usage/num_turns at end of a reply. */
  lastResult?: ResultFrame;

  /**
   * Footer-level work state for the current human turn.
   *
   * ``ResultFrame`` can arrive at iteration boundaries inside one
   * Claude Code turn. This state keeps the footer in "Working..."
   * across those boundaries and only freezes "Worked for X" once the
   * whole turn is actually idle.
   */
  turnWork: AgentTurnWorkState;

  /** Cumulative cost across every result this session has seen. */
  cumulativeCostUsd: number;

  /** System-level notices — compact boundary, api retry. */
  notices: SystemNotice[];

  /**
   * Slash command names advertised by the spawned CLI on
   * ``system.init`` (without the leading ``/``). Includes built-ins
   * like ``compact`` / ``context`` and any project- or user-scope
   * commands the CLI discovered via the standard settings stack.
   * Surfaced by :class:`AgentInput` as an autocomplete popover when
   * the user types ``/`` at the start of the textarea.
   */
  slashCommands: string[];

  /** Server-reported error strings. */
  errors: string[];
  /** Turn-local errors waiting for the user prompt they belong to to be loaded from history. */
  pendingAnchoredErrors: Turn[];

  /**
   * True from the moment the user sends a message until the assistant
   * either starts replying (``message_start`` / ``assistant`` frame) or
   * the request terminates (``result`` frame). Drives the bouncing-dot
   * "thinking" placeholder in :class:`TurnStream` so the chat feels
   * reactive even before the first token arrives.
   */
  awaitingAssistant: boolean;

  /** Derived status — cheap to recompute but memoized by reducer on each frame. */
  status: AgentStatus;

  /**
   * ms epoch of the most recent frame that mutated user-visible
   * conversation content. Updated every time the reducer folds an
   * ``assistant`` / ``user`` / ``result`` frame, the streaming
   * variants of those, or a tool result. Drives the sidebar's
   * unread dot — a session is "unread" when its ``lastEventAt``
   * grew past the user's per-session "seen" baseline.
   *
   * Lifecycle frames (``session_attached``, ``system.init``,
   * ``rate_limit``) do NOT bump this — the user already saw the
   * session, watching the SDK reattach is not "new activity."
   */
  lastEventAt: number;

  /**
   * Running high-water mark of context tokens used in this
   * session — ``input + cache_creation + cache_read`` from
   * whichever turn had the largest fill. Drives the context
   * chip: per-turn usage objects can swing wildly based on
   * cache-hit patterns and which iteration the SDK reports, but
   * the **conversation only grows** until an explicit compaction.
   * Tracking the max stabilizes the chip so a small follow-up
   * after a heavy tool call doesn't make it look like context
   * shrunk by half.
   *
   * Reset on ``system.compact_boundary`` (the only event that
   * legitimately shrinks the conversation) and hydrated from the
   * server's full-transcript session snapshot on attach.
   */
  contextTokensHighWater: number;

  /**
   * Monotonically increasing counter incremented on every
   * ``system.compact_boundary`` frame (manual or auto). Stamped onto
   * every newly-created assistant ``Turn`` via :attr:`Turn.compactEpoch`
   * so the high-water walk in :func:`_updateContextHighWater` can
   * exclude turns whose ``usage`` predates the most recent compact.
   * Without this, the walk repeatedly re-asserts the pre-compact
   * usage value (the latest assistant turn before compaction still
   * lives in ``state.turns``) and the context chip never drops
   * after ``/compact``.
   */
  compactEpoch: number;

  /**
   * ms epoch at which we observed the user submit ``/compact`` and
   * are still waiting for the matching ``system.compact_boundary``
   * frame from the SDK. Drives a dedicated "Compacting…" indicator
   * in place of the generic Working state -- manual ``/compact``
   * runs synchronously inside the CLI, emits no per-iteration
   * traffic, and otherwise leaves the user staring at "Working…"
   * with no signal that anything is happening.
   *
   * Cleared on the first ``system.compact_boundary`` frame regardless
   * of trigger, OR by :func:`isCompactPending` once the wall-clock
   * timeout (60s) has elapsed -- self-clearing so a missed boundary
   * frame can't permanently latch the indicator on.
   */
  pendingCompactSince: number | null;

  /**
   * ms epoch at which the user clicked Stop and we sent an
   * ``interrupt`` frame. Until the server's terminal ``result`` frame
   * lands (or :const:`INTERRUPT_PENDING_TIMEOUT_MS` elapses), any
   * in-flight SDK frames the server emitted before processing the
   * interrupt (``message_start``, ``content_block_delta``, ``user``
   * echo, ...) must NOT re-arm ``turnWork`` / ``status`` / the busy
   * indicator. Without this, the user observes the Stop button
   * disappearing on click then flashing back the moment those
   * already-in-flight frames arrive, and they have to click Stop
   * twice to actually catch the agent.
   *
   * Cleared on:
   *   - any ``result`` frame (server confirmed the turn ended)
   *   - any new optimistic user send (the user moved on)
   *   - ``resetAttached`` (reconnect resets all transient state)
   *   - timeout via :func:`isInterruptPending` (failsafe if the
   *     server never sends a terminal frame, e.g. crash mid-turn)
   */
  interruptPendingSince: number | null;
}

export function initialState(): AgentPanelState {
  return {
    attached: false,
    cwd: null,
    sessionId: null,
    // Seed the expected default model so the footer paints before
    // ``session_attached`` / ``system.init`` land. Both server frames
    // remain authoritative and overwrite this when present.
    model: DEFAULT_DISPLAY_MODEL,
    // Seed the reasoning level so :class:`EffortPill` renders the
    // moment the overlay opens — long before ``session_attached``
    // arrives. The ``set_session_options`` reducer overwrites this
    // when the server reports the actual mode.
    effort: 'xhigh',
    speed: null,
    permissionMode: 'bypassPermissions',
    protocolVersion: null,
    capabilities: [],
    turns: [],
    activeStreamTurnId: null,
    toolResults: {},
    subagents: {},
    toolUseRegistry: {},
    activity: emptyActivity(),
    pendingInteractions: [],
    turnWork: emptyTurnWork(),
    cumulativeCostUsd: 0,
    notices: [],
    errors: [],
    pendingAnchoredErrors: [],
    slashCommands: [],
    awaitingAssistant: false,
    status: 'idle',
    lastEventAt: 0,
    contextTokensHighWater: 0,
    compactEpoch: 0,
    pendingCompactSince: null,
    interruptPendingSince: null,
  };
}

/**
 * Wall-clock timeout for the optimistic ``pendingCompactSince`` flag.
 * Manual ``/compact`` typically completes in 3-5s; a 60s ceiling means
 * a missed ``compact_boundary`` frame still self-clears the indicator
 * within the same conversation rather than latching it forever.
 */
const PENDING_COMPACT_TIMEOUT_MS = 60_000;

/**
 * Whether the agent panel is currently waiting on a ``/compact``
 * to complete. Self-clearing -- if the boundary frame never arrives
 * (process crash, dropped WebSocket), the indicator falls back to
 * the normal Working/idle state once :const:`PENDING_COMPACT_TIMEOUT_MS`
 * has elapsed since the user submitted the command.
 */
export function isCompactPending(state: AgentPanelState, now: number = Date.now()): boolean {
  if (state.pendingCompactSince === null) return false;
  return now - state.pendingCompactSince < PENDING_COMPACT_TIMEOUT_MS;
}

/**
 * Wall-clock timeout for the optimistic ``interruptPendingSince`` flag.
 * The server's terminal ``result`` frame is the authoritative end of
 * an interrupted turn; a 5s ceiling protects against the edge case
 * where the server never emits one (SDK crash, transport drop) so
 * the busy indicator can't latch off forever.
 */
const INTERRUPT_PENDING_TIMEOUT_MS = 5_000;

/**
 * Whether the user has just hit Stop and the server's terminal frame
 * for the interrupted turn has not yet landed. While true, in-flight
 * SDK frames the server emitted before processing the interrupt are
 * displayed without re-arming the busy state -- the user only had
 * to click Stop once.
 */
export function isInterruptPending(state: AgentPanelState, now: number = Date.now()): boolean {
  if (state.interruptPendingSince === null) return false;
  return now - state.interruptPendingSince < INTERRUPT_PENDING_TIMEOUT_MS;
}

/**
 * Optimistically insert a user turn into the chat the instant the user
 * hits send, *before* the WebSocket frame leaves the browser. The
 * server later echoes the same message back as a ``user`` frame; the
 * reducer reconciles in place (see :func:`_onUser`) so the chat never
 * flickers and the user's bubble can never appear *below* the
 * assistant's reply, even if the SDK happens to yield ``stream_event``
 * before the replayed ``user`` message.
 *
 * Call this *before* :func:`AgentTransport.send` so that the optimistic
 * turn is in state by the time React schedules the next render.
 */
export function applyOptimisticUserSend(
  state: AgentPanelState,
  text: string,
  display?: TurnDisplay,
  clientMessageId?: string
): AgentPanelState {
  const trimmed = text.trim();
  if (!trimmed) return state;
  const now = Date.now();
  const awaitsAssistant = _newUserTurnAwaitsAssistant(state);
  const optimisticTurn = _optimisticUserTurn(trimmed, now, display, undefined, awaitsAssistant, clientMessageId);
  const activeTurnWork =
    state.turnWork.startedAt !== null && state.turnWork.completedDurationMs === null
      ? state.turnWork
      : { startedAt: now, completedDurationMs: null };
  // Clear any stale errors / rate-limit rejections on a fresh send —
  // the user is moving on, the banner shouldn't follow them.
  // ``/compact`` is local CLI work with no per-iteration traffic, so
  // arm the dedicated "Compacting…" indicator the moment we observe
  // the send. The flag self-clears either on the matching
  // ``compact_boundary`` frame or after :const:`PENDING_COMPACT_TIMEOUT_MS`
  // wall-clock (see :func:`isCompactPending`).
  const pendingCompactSince = COMPACT_COMMAND_RE.test(trimmed) ? now : state.pendingCompactSince;
  const next: AgentPanelState = {
    ...state,
    turns: [...state.turns, optimisticTurn],
    awaitingAssistant: true,
    turnWork: activeTurnWork,
    errors: [],
    pendingInteractions: [],
    rateLimit: state.rateLimit?.status === 'rejected' ? undefined : state.rateLimit,
    lastEventAt: now,
    pendingCompactSince,
    // The user moved on -- whatever was being interrupted no longer
    // matters for busy-state suppression. Clearing here also covers
    // the case where the server never emitted a terminal frame for
    // the interrupted turn (so the timeout would have been the only
    // path out).
    interruptPendingSince: null,
  };
  return { ...next, status: _deriveStatus(next) };
}

export function applyOptimisticInteractionResponse(state: AgentPanelState, requestId: string): AgentPanelState {
  const id = requestId.trim();
  if (!id) return state;
  const pendingInteractions = state.pendingInteractions.filter((item) => item.request_id !== id);
  return pendingInteractions.length === state.pendingInteractions.length ? state : { ...state, pendingInteractions };
}

function _optimisticUserTurn(
  text: string,
  startedAt: number,
  display?: TurnDisplay,
  id?: string,
  awaitsAssistant = true,
  clientMessageId?: string
): Turn {
  return {
    id: id ?? `optimistic-user-${startedAt}-${Math.random().toString(36).slice(2, 8)}`,
    role: 'user',
    blocks: { 0: { block_type: 'TextBlock', text, citations: null } },
    order: [0],
    partialToolInput: {},
    blockTimings: {},
    committed: true,
    startedAt,
    optimistic: true,
    awaitsAssistant,
    display,
    clientMessageId,
  };
}

function _newUserTurnAwaitsAssistant(state: AgentPanelState): boolean {
  return !(
    state.awaitingAssistant ||
    state.activeStreamTurnId !== null ||
    state.status === 'thinking' ||
    state.status === 'streaming' ||
    state.status === 'tool_running'
  );
}

/**
 * Stop the current turn.
 *
 * Claude Code persists a synthetic ``[Request interrupted by user...]``
 * user message after ``client.interrupt()``; the server drops that
 * envelope, and the webapp should not invent a replacement transcript
 * row. Interrupt only clears local pending state so live and refreshed
 * chat history remain consistent with JSONL.
 */
export function applyInterrupt(state: AgentPanelState): AgentPanelState {
  const target = _findInterruptTargetIndex(state);
  let turns = state.turns;
  const interruptedToolIds = target >= 0 ? _pendingToolIdsInTurn(state, state.turns[target]) : [];
  if (target >= 0) {
    turns = state.turns.slice();
    turns[target] = { ...turns[target], committed: true };
  }

  let toolResults = state.toolResults;
  for (const toolUseId of interruptedToolIds) {
    if (toolResults[toolUseId] !== undefined) continue;
    if (toolResults === state.toolResults) toolResults = { ...state.toolResults };
    toolResults[toolUseId] = {
      block_type: 'ToolResultBlock',
      tool_use_id: toolUseId,
      content: 'Stopped by user',
      is_error: true,
    };
  }

  // Freeze the elapsed work time the same way a natural turn-end
  // does (see ``_finalizeTurnWork``) so the footer-level marker
  // transitions cleanly from a shimmering "Working" to a frozen
  // "Worked for Ns" instead of unmounting. Unmounting at the same
  // instant the active assistant turn loses its held thinking
  // block (committed=true → ``keepThinkingVisible`` flips false)
  // gives a flash of simultaneous component teardowns; preserving
  // the duration keeps one stable anchor visible through the
  // transition.
  const interruptedTurnWork: AgentTurnWorkState =
    state.turnWork.startedAt !== null
      ? { startedAt: null, completedDurationMs: Math.max(0, Date.now() - state.turnWork.startedAt) }
      : emptyTurnWork();

  const next: AgentPanelState = {
    ...state,
    turns,
    toolResults,
    awaitingAssistant: false,
    activeStreamTurnId: null,
    turnWork: interruptedTurnWork,
    // Suppress busy-state re-arming from any SDK frames the server
    // emitted before processing the interrupt. Cleared by ``_onResult``
    // (the server's authoritative end-of-turn signal), a subsequent
    // ``applyOptimisticUserSend`` (user moved on), or the wall-clock
    // timeout in :func:`isInterruptPending`. See the field's docstring
    // on :type:`AgentPanelState` for the full rationale.
    interruptPendingSince: Date.now(),
  };
  return { ...next, status: _deriveStatus(next) };
}

function _findInterruptTargetIndex(state: AgentPanelState): number {
  for (let i = state.turns.length - 1; i >= 0; i--) {
    const turn = state.turns[i];
    if (turn.role === 'assistant' && !turn.committed) return i;
  }
  for (let i = state.turns.length - 1; i >= 0; i--) {
    const turn = state.turns[i];
    if (turn.role === 'assistant' && _turnHasPendingTool(state, turn)) return i;
  }
  return -1;
}

function _pendingToolIdsInTurn(state: AgentPanelState, turn: Turn): string[] {
  const ids: string[] = [];
  for (const index of turn.order) {
    const block = turn.blocks[index];
    if (block?.block_type !== 'ToolUseBlock' && block?.block_type !== 'ServerToolUseBlock') continue;
    const toolUseId = (block as ToolUseBlock).id;
    if (toolUseId && isToolUsePending(state, toolUseId)) ids.push(toolUseId);
  }
  return ids;
}

function _turnHasPendingTool(state: AgentPanelState, turn: Turn): boolean {
  return turn.order.some((i) => {
    const block = turn.blocks[i];
    if (block?.block_type !== 'ToolUseBlock' && block?.block_type !== 'ServerToolUseBlock') return false;
    return isToolUsePending(state, (block as ToolUseBlock).id);
  });
}

/**
 * Merge a page of historical frames with ``state.turns``.
 *
 * The model is deliberately narrow:
 *
 * 1. **Tail load** (``state.turns`` empty or only optimistic turns) —
 *    historical frames become the transcript; optimistic bubbles are
 *    paired to their persisted counterparts by ``client_message_id``.
 * 2. **Older page** (``state.turns`` already populated by a previous
 *    REST page + live frames) — historical frames are PREPENDED to the
 *    existing window; any historical turn whose id is already in
 *    ``state.turns`` is replaced in place so live content (which is
 *    fresher) wins on overlap.
 *
 * The frames must be in chronological order (oldest first). The
 * server's ``FrameRing`` no longer replays the transcript on a fresh
 * attach, so ``state.turns`` cannot contain frames older than the
 * REST window — the merge is a clean concatenation, not a
 * reordering of overlapping ring frames into the historical prefix.
 */
export function prependHistory(state: AgentPanelState, frames: OutboundFrame[]): AgentPanelState {
  if (frames.length === 0) return state;

  // Build the historical turns through the same reducer that handles
  // live frames so turn shape, tool result routing, and activity
  // bookkeeping stay in one place.
  let scratch: AgentPanelState = initialState();
  for (const frame of frames) {
    scratch = _reduce(scratch, frame);
  }

  // Pair optimistic bubbles with the persisted user row that mirrors
  // them, by ``clientMessageId``. The server stamps that id on every
  // user frame we submitted, so this is an exact match — no text
  // matching, no FIFO window.
  const stateForMerge = _reconcileHistoryByClientMessageId(state, scratch.turns);

  // Build an id index for the live window so any overlap with the
  // historical page replaces the historical placeholder with the
  // freshest live version at the historical position.
  const liveById = new Map<string, Turn>();
  for (const t of stateForMerge.turns) {
    if (t.id) liveById.set(t.id, t);
    if (t.messageId) liveById.set(t.messageId, t);
  }
  const matchedLiveIds = new Set<string>();

  const orderedHistorical: Turn[] = [];
  for (const histTurn of scratch.turns) {
    const liveMatch = liveById.get(histTurn.id) ?? (histTurn.messageId ? liveById.get(histTurn.messageId) : undefined);
    if (liveMatch) {
      orderedHistorical.push(liveMatch);
      matchedLiveIds.add(liveMatch.id);
      if (liveMatch.messageId) matchedLiveIds.add(liveMatch.messageId);
    } else {
      orderedHistorical.push(histTurn);
    }
  }

  // Anything in ``state.turns`` the historical page did not reference
  // is a live-only turn — an in-progress assistant stream, an
  // optimistic bubble waiting on its echo, or a frame received after
  // the REST snapshot was captured. Append it after the historical
  // prefix in its existing relative order. Synthetic-id user turns
  // (live frames the SDK emitted without a uuid, or queue-enqueue
  // rows converted to wire frames) are dropped here when their text
  // is already in the historical prefix — they'd render as duplicate
  // bubbles otherwise.
  let finalTurns = orderedHistorical;
  const historicalUserTexts = _userTurnTextSet(orderedHistorical);
  let pendingAnchoredErrors = stateForMerge.pendingAnchoredErrors;
  for (const liveTurn of stateForMerge.turns) {
    if (matchedLiveIds.has(liveTurn.id)) continue;
    if (liveTurn.messageId && matchedLiveIds.has(liveTurn.messageId)) continue;
    if (_isDuplicateLiveOnlyUser(liveTurn, historicalUserTexts)) continue;
    if (liveTurn.errorForUserText) {
      const placed = _insertOrQueueAnchoredError(finalTurns, pendingAnchoredErrors, liveTurn);
      finalTurns = placed.turns;
      pendingAnchoredErrors = placed.pendingAnchoredErrors;
    } else {
      finalTurns = [...finalTurns, liveTurn];
    }
  }
  if (pendingAnchoredErrors.length > 0) {
    const placed = _flushPendingAnchoredErrors(finalTurns, pendingAnchoredErrors);
    finalTurns = placed.turns;
    pendingAnchoredErrors = placed.pendingAnchoredErrors;
  }

  const toolResults: Record<string, ToolResultBlock> = { ...stateForMerge.toolResults };
  for (const [id, block] of Object.entries(scratch.toolResults)) {
    if (!(id in toolResults)) toolResults[id] = block;
  }

  // Carry historical tool-use names + checklist state forward so the
  // todo chip and tool labels survive a refresh that rebuilt history
  // from JSONL while the live state still treats them as authoritative.
  const toolUseRegistry: Record<string, string> = { ...scratch.toolUseRegistry, ...stateForMerge.toolUseRegistry };
  const activity: ActivityState = {
    ...stateForMerge.activity,
    plan: stateForMerge.activity.plan ?? scratch.activity.plan,
  };

  // Stable-reference no-op when nothing actually changed; the React
  // store relies on referential equality to skip re-renders.
  const turnsEqual =
    finalTurns.length === stateForMerge.turns.length && finalTurns.every((t, i) => t === stateForMerge.turns[i]);
  const toolResultsEqual = Object.keys(toolResults).length === Object.keys(stateForMerge.toolResults).length;
  const pendingErrorsEqual =
    pendingAnchoredErrors.length === stateForMerge.pendingAnchoredErrors.length &&
    pendingAnchoredErrors.every((turn, i) => turn === stateForMerge.pendingAnchoredErrors[i]);
  if (turnsEqual && toolResultsEqual && pendingErrorsEqual && stateForMerge === state) {
    return state;
  }

  const next: AgentPanelState = {
    ...stateForMerge,
    turns: finalTurns,
    toolResults,
    toolUseRegistry,
    activity,
    pendingAnchoredErrors,
  };
  return { ...next, status: _deriveStatus(next) };
}

/**
 * Pair every optimistic user turn with its persisted counterpart from
 * the historical page.
 *
 * Primary pass uses ``clientMessageId`` — the server stamps that id on
 * every live ``user`` frame the webapp submitted, so when it lands on
 * a history row the match is exact (no text comparisons, no
 * timestamps). The SDK persists JSONL ahead of the server stamping
 * the live frame, though, so history rows hydrated from an older
 * persistence pass may NOT carry the id. The fallback pass pairs any
 * still-pending optimistic by text content, anchoring at the END of
 * history so a freshly-typed "ok" lands on the just-persisted row
 * rather than an ancient duplicate further up the transcript.
 */
function _reconcileHistoryByClientMessageId(state: AgentPanelState, historyTurns: Turn[]): AgentPanelState {
  const pendingTurns = state.turns.filter((turn) => turn.role === 'user' && turn.optimistic === true);
  if (pendingTurns.length === 0) return state;

  // Pass 1: exact id match.
  const pendingById = new Map<string, Turn>();
  for (const turn of pendingTurns) {
    if (turn.clientMessageId) pendingById.set(turn.clientMessageId, turn);
  }
  const consumedHistoryIndices = new Set<number>();
  let nextState = state;
  if (pendingById.size > 0) {
    for (let i = 0; i < historyTurns.length; i++) {
      const historyTurn = historyTurns[i];
      if (historyTurn.role !== 'user') continue;
      const clientMessageId = historyTurn.clientMessageId;
      if (!clientMessageId) continue;
      const optimistic = pendingById.get(clientMessageId);
      if (!optimistic) continue;
      pendingById.delete(clientMessageId);
      consumedHistoryIndices.add(i);
      const idx = nextState.turns.indexOf(optimistic);
      if (idx < 0) continue;
      const turns = nextState.turns.slice();
      turns[idx] = _mergeOptimisticWithHistory(optimistic, historyTurn);
      nextState = { ...nextState, turns };
    }
  }

  // Pass 2: text fallback for optimistic turns the server did not
  // stamp (typically because the SDK persisted JSONL before the live
  // frame was emitted). Group by text and pair each text's optimistic
  // queue with the LAST K history matches in chronological order.
  const stillPending = nextState.turns.filter((turn) => turn.role === 'user' && turn.optimistic === true);
  if (stillPending.length === 0) return nextState;
  const pendingByText = new Map<string, Turn[]>();
  for (const turn of stillPending) {
    const text = _turnPlainText(turn);
    if (!text) continue;
    let queue = pendingByText.get(text);
    if (!queue) {
      queue = [];
      pendingByText.set(text, queue);
    }
    queue.push(turn);
  }
  if (pendingByText.size === 0) return nextState;
  for (const [text, optimisticQueue] of pendingByText) {
    const recentMatches: Array<{ turn: Turn; index: number }> = [];
    for (let i = historyTurns.length - 1; i >= 0 && recentMatches.length < optimisticQueue.length; i--) {
      if (consumedHistoryIndices.has(i)) continue;
      const candidate = historyTurns[i];
      if (candidate.role !== 'user') continue;
      if (_turnPlainText(candidate) !== text) continue;
      recentMatches.unshift({ turn: candidate, index: i });
    }
    const pairCount = Math.min(optimisticQueue.length, recentMatches.length);
    for (let i = 0; i < pairCount; i++) {
      const optimistic = optimisticQueue[i];
      const historyMatch = recentMatches[i];
      consumedHistoryIndices.add(historyMatch.index);
      const idx = nextState.turns.indexOf(optimistic);
      if (idx < 0) continue;
      const turns = nextState.turns.slice();
      turns[idx] = _mergeOptimisticWithHistory(optimistic, historyMatch.turn);
      nextState = { ...nextState, turns };
    }
  }
  return nextState;
}

function _mergeOptimisticWithHistory(optimistic: Turn, historyTurn: Turn): Turn {
  return {
    ...optimistic,
    messageId: historyTurn.messageId ?? historyTurn.id,
    blocks: historyTurn.blocks,
    order: historyTurn.order,
    committed: true,
    optimistic: false,
  };
}

function _userTurnTextSet(turns: Turn[]): Set<string> {
  const texts = new Set<string>();
  for (const turn of turns) {
    if (turn.role !== 'user') continue;
    const text = _turnPlainText(turn);
    if (text) texts.add(text);
  }
  return texts;
}

/**
 * ``true`` for live-only user turns that should be dropped during a
 * history merge because the historical prefix already shows the same
 * text and the live turn has a synthetic id (so it cannot be the
 * same row idempotently surfaced through the id index). Catches
 * frames the SDK emitted without a uuid and queue-enqueue rows that
 * the server converted to ``queued-user-*`` wire frames.
 */
function _isDuplicateLiveOnlyUser(turn: Turn, historicalUserTexts: Set<string>): boolean {
  if (turn.role !== 'user') return false;
  const text = _turnPlainText(turn);
  if (!text || !historicalUserTexts.has(text)) return false;
  const id = turn.id ?? '';
  const hasSyntheticIdentity = !turn.messageId || id.startsWith('queued-user-') || id.startsWith('user-');
  return hasSyntheticIdentity;
}

function _turnPlainText(turn: Turn): string {
  return turnBlocks(turn)
    .filter((block): block is TextBlock => block.block_type === 'TextBlock')
    .map((block) => block.text.trim())
    .filter(Boolean)
    .join('\n');
}

/**
 * Hydrate the perpetual activity slice from a server-derived snapshot.
 *
 * The snapshot is the authoritative running-set for background tasks:
 * ``active_tasks`` REPLACES the live state's ``activity.activeTasks``
 * (no per-task merge, no notification deque replay). The server's
 * ``SessionStateBuilder`` is the single source of truth. The optional
 * ``preserveActiveTasksStartedAfter`` and
 * ``activeTaskIdsAtRequest`` are only for the initial-history race
 * where live ``task_started`` / ``task_notification`` frames land
 * after the REST request was issued but before its older snapshot is
 * reduced.
 */
export function applySessionStateSnapshot(
  state: AgentPanelState,
  snapshot: {
    is_active?: boolean;
    last_completed_turn_duration_ms?: number | null;
    active_turn_started_at_ms?: number | null;
    context_tokens_high_water?: number;
    latest_todo_write: { tool_use_id: string; input: Record<string, unknown> } | null;
    latest_task_state?: { tool_use_id: string; tasks: Record<string, unknown>[] } | null;
    latest_exit_plan_mode?: { tool_use_id: string; input: Record<string, unknown> } | null;
    active_tasks?: TaskStartedFrame[];
    interrupted_tool_results?: ToolResultBlock[];
  },
  options: {
    preserveActiveTasksStartedAfter?: number;
    activeTaskIdsAtRequest?: ReadonlySet<string>;
    contextTokensHighWaterAtRequest?: number;
    /** Task ids seen to terminate via a live frame; never resurrect them. */
    completedTaskIds?: ReadonlySet<string>;
  } = {}
): AgentPanelState {
  let toolUseRegistry = state.toolUseRegistry;
  let toolResults = state.toolResults;
  let awaitingAssistant = state.awaitingAssistant;
  let activeStreamTurnId = state.activeStreamTurnId;
  let turnWork = state.turnWork;
  let contextTokensHighWater = state.contextTokensHighWater;

  // Rebuild ``activeTasks`` from the snapshot. Each entry runs through
  // the same categorization the live ``_onTaskStarted`` uses so chip
  // counts stay consistent. Subagent rows are not part of the
  // snapshot — the server treats ``Task`` agents as foreground tools.
  const activeTasks: Record<string, ActiveTask> = {};
  const activeTaskIdsAtRequest = options.activeTaskIdsAtRequest;
  for (const task of snapshot.active_tasks ?? []) {
    if (!task.task_id) continue;
    // Never resurrect a task we've already seen terminate via a live frame:
    // this snapshot may be a stale server view captured before the completion.
    if (options.completedTaskIds?.has(task.task_id)) continue;
    if (activeTaskIdsAtRequest?.has(task.task_id) && state.activity.activeTasks[task.task_id] === undefined) {
      continue;
    }
    activeTasks[task.task_id] = _activeTaskFromSnapshot(state, task, state.activity.activeTasks[task.task_id]);
  }
  const preserveStartedAfter = options.preserveActiveTasksStartedAfter;
  if (preserveStartedAfter !== undefined) {
    for (const task of Object.values(state.activity.activeTasks)) {
      if (activeTasks[task.taskId] !== undefined) continue;
      if (task.startedAt >= preserveStartedAfter) activeTasks[task.taskId] = task;
    }
  }
  let activity: ActivityState = _sameActiveTaskRefs(activeTasks, state.activity.activeTasks)
    ? state.activity
    : _withRecountedActivity({ ...state.activity, activeTasks });

  for (const result of snapshot.interrupted_tool_results ?? []) {
    const toolUseId = typeof result?.tool_use_id === 'string' ? result.tool_use_id : '';
    if (!toolUseId || toolResults[toolUseId] !== undefined) continue;
    if (toolResults === state.toolResults) toolResults = { ...toolResults };
    toolResults[toolUseId] = {
      block_type: 'ToolResultBlock',
      tool_use_id: toolUseId,
      content: result.content ?? 'Stopped by user',
      is_error: result.is_error ?? true,
    };
  }

  const taskState = snapshot.latest_task_state;
  if (taskState && activity.plan === null) {
    const plan = _readPlanFromTaskState(taskState.tool_use_id, taskState.tasks);
    if (plan && !_isClearedPlan(plan)) {
      activity = { ...activity, plan };
      if (taskState.tool_use_id) {
        toolUseRegistry = { ...toolUseRegistry, [taskState.tool_use_id]: 'TaskList' };
      }
    }
  }

  const todo = snapshot.latest_todo_write;
  if (todo && activity.plan === null) {
    if (!_isClearedTodoList((todo.input as Record<string, unknown>).todos)) {
      const plan = _readPlanFromTodoWrite(todo.tool_use_id, todo.input);
      if (plan) {
        activity = { ...activity, plan };
        if (todo.tool_use_id) {
          toolUseRegistry = { ...toolUseRegistry, [todo.tool_use_id]: 'TodoWrite' };
        }
      }
    }
  }

  const completedDurationMs =
    'last_completed_turn_duration_ms' in snapshot ? _validDurationMs(snapshot.last_completed_turn_duration_ms) : null;
  const snapshotIsActive = snapshot.is_active === true && completedDurationMs === null;
  const snapshotIsInactive = snapshot.is_active === false;
  const stateForBusyCheck = toolResults === state.toolResults ? state : { ...state, toolResults };

  // Optimistic-busy preservation: if the user just submitted a
  // message that hasn't been reconciled by the server yet
  // (``optimistic === true`` on the tail user turn) OR a submitted
  // tail user turn is still waiting on the assistant, this snapshot
  // is stale w.r.t. the optimistic update -- the round-trip ``GET
  // /sessions/messages`` was in flight when the user pressed send,
  // so its ``is_active=false`` / ``last_completed_turn_duration_ms``
  // reflect the PREVIOUS turn's completion, not the new one.
  //
  // Without this guard, the periodic ``_refreshSessionState`` poll
  // (or the post-attach hydrate) regresses ``awaitingAssistant`` to
  // ``false`` and resurrects the previous "Worked for 37s" footer
  // for 4-5s right after the user hits send -- exactly the
  // "indicator lags before flipping to Working..." bug reported
  // against this code path.
  //
  // We still let snapshot data ADD busy state (``snapshotIsActive``
  // branch below) -- that's the recovery path when the server saw
  // a send the browser missed (reconnect, dropped frames). Only the
  // regression direction (true -> false) is gated.
  const optimisticSubmittedUserAwaits = _submittedUserAwaitsAssistant(state);

  if (snapshotIsActive && !awaitingAssistant && _tailLooksBusyFromHistory(stateForBusyCheck)) {
    awaitingAssistant = true;
  } else if (!optimisticSubmittedUserAwaits && (completedDurationMs !== null || snapshotIsInactive)) {
    awaitingAssistant = false;
  }

  if ('last_completed_turn_duration_ms' in snapshot) {
    const durationMs = snapshot.last_completed_turn_duration_ms;
    if (snapshotIsActive || durationMs === null) {
      turnWork = { ...turnWork, completedDurationMs: null };
    } else if (!optimisticSubmittedUserAwaits && completedDurationMs !== null) {
      turnWork = { startedAt: null, completedDurationMs };
    }
  }
  if (snapshotIsInactive && !optimisticSubmittedUserAwaits) {
    activeStreamTurnId = null;
    if (turnWork.startedAt !== null) {
      turnWork = { ...turnWork, startedAt: null };
    }
  }
  if (snapshotIsActive) {
    const serverStartedAt = _validDurationMs(snapshot.active_turn_started_at_ms);
    const startedAt = serverStartedAt !== null ? Math.min(Date.now(), serverStartedAt) : turnWork.startedAt;
    turnWork = { startedAt, completedDurationMs: null };
  }
  if (typeof snapshot.context_tokens_high_water === 'number' && Number.isFinite(snapshot.context_tokens_high_water)) {
    const snapshotHighWater = Math.max(0, Math.floor(snapshot.context_tokens_high_water));
    const requestHighWater = options.contextTokensHighWaterAtRequest;
    // The snapshot is authoritative for everything before the REST
    // request. Preserve only live context movement that happened while
    // that request was in flight.
    if (requestHighWater === undefined) {
      contextTokensHighWater = snapshotHighWater;
    } else if (state.contextTokensHighWater > requestHighWater) {
      contextTokensHighWater = Math.max(snapshotHighWater, state.contextTokensHighWater);
    } else if (state.contextTokensHighWater < requestHighWater) {
      contextTokensHighWater = state.contextTokensHighWater;
    } else {
      contextTokensHighWater = snapshotHighWater;
    }
  }

  if (
    activity === state.activity &&
    toolUseRegistry === state.toolUseRegistry &&
    toolResults === state.toolResults &&
    awaitingAssistant === state.awaitingAssistant &&
    activeStreamTurnId === state.activeStreamTurnId &&
    turnWork === state.turnWork &&
    contextTokensHighWater === state.contextTokensHighWater
  ) {
    return state;
  }
  const next = {
    ...state,
    activity,
    toolUseRegistry,
    toolResults,
    awaitingAssistant,
    activeStreamTurnId,
    turnWork,
    contextTokensHighWater,
  };
  return { ...next, status: _deriveStatus(next) };
}

function _tailLooksBusyFromHistory(state: AgentPanelState): boolean {
  const last = state.turns[state.turns.length - 1];
  if (!last) return false;
  if (!last.committed) return true;
  if (last.role === 'user') return true;
  if (last.role !== 'assistant') return false;
  return last.order.some((i) => {
    const b = last.blocks[i];
    if (b?.block_type !== 'ToolUseBlock' && b?.block_type !== 'ServerToolUseBlock') return false;
    return state.toolResults[(b as ToolUseBlock).id] === undefined;
  });
}

/**
 * User-initiated dismissal of the error banner. Clears both the
 * accumulated stream / server errors and any sticky rate-limit
 * rejection so the banner goes away. The next inbound error frame
 * will repopulate it.
 */
export function clearErrors(state: AgentPanelState): AgentPanelState {
  if (state.errors.length === 0 && state.rateLimit?.status !== 'rejected') return state;
  return {
    ...state,
    errors: [],
    rateLimit: state.rateLimit?.status === 'rejected' ? undefined : state.rateLimit,
  };
}

// =============================================================================
// Reducer entry
// =============================================================================

/**
 * Frame types that bump :attr:`AgentPanelState.lastEventAt`.
 *
 * Only frames that mutate user-visible conversation content qualify:
 * any assistant text / tool / thinking, the user's own echoed
 * messages, tool results, and final ``result`` frames. Lifecycle
 * frames (``session_attached``, ``system.init``, ``rate_limit``)
 * do *not* count as "new activity" — the user
 * already saw the session, watching the SDK reattach is not
 * something the unread badge should fire on.
 */
const _USER_VISIBLE_FRAME_TYPES: ReadonlySet<OutboundFrame['type']> = new Set([
  'assistant',
  'user',
  'stream_event',
  'result',
  'task_started',
  'task_progress',
  'task_notification',
  'interaction_request',
  'interaction_state',
  'server_error',
]);

/**
 * Sentinel ``model`` value Claude Code stamps on the assistant
 * frames it injects when an upstream API call fails (e.g. the 401
 * "Invalid bearer token" message it persists into the JSONL when
 * the auth helper returns a stale token). Treating those as user-
 * visible activity would re-light the sidebar's unread dot every
 * time auth flaps, even though the user did nothing and the agent
 * never produced real content.
 */
const _SYNTHETIC_MODEL = '<synthetic>';
const _NO_RESPONSE_REQUESTED_RE = /^no response requested\.?$/i;
const _BACKGROUND_TASK_RESULT_RE = /Command running in background with ID:\s*(?<taskId>[A-Za-z0-9_-]+)/i;

/**
 * ``true`` when ``frame`` should NOT bump
 * :attr:`AgentPanelState.lastEventAt`. Currently filters synthetic
 * assistant frames; extend here if Claude Code grows other
 * placeholder frame variants we want to suppress.
 */
function _isSyntheticFrame(frame: OutboundFrame): boolean {
  return frame.type === 'assistant' && frame.model === _SYNTHETIC_MODEL;
}

/**
 * Extract the error payload from Claude Code synthetic assistant frames.
 *
 * Those frames are persisted as TextBlocks even though they describe an
 * upstream failure, not agent-authored prose. Rendering them through
 * ``Turn.error`` lets the chat use the designed notice treatment instead.
 */
function _syntheticAssistantError(frame: AssistantFrame, content: ContentBlock[]): string | null {
  if (frame.model !== _SYNTHETIC_MODEL) return null;
  const text = content
    .filter((block): block is TextBlock => block.block_type === 'TextBlock')
    .map((block) => block.text.trim())
    .filter(Boolean)
    .join('\n\n');
  return text || frame.error || null;
}

function _isNoResponseRequested(error: string | null): boolean {
  return error !== null && _NO_RESPONSE_REQUESTED_RE.test(error.trim());
}

export function reduce(state: AgentPanelState, frame: OutboundFrame): AgentPanelState {
  const next = _reduce(state, frame);
  if (next === state) return state;
  const shouldStamp = _USER_VISIBLE_FRAME_TYPES.has(frame.type) && !_isSyntheticFrame(frame);
  const withoutStaleRetry = frame.type === 'system.api_retry' ? next : _dropApiRetryNotices(next);
  const stamped = shouldStamp ? { ...withoutStaleRetry, lastEventAt: Date.now() } : withoutStaleRetry;
  const withHighWater = _updateContextHighWater(stamped, frame);
  return { ...withHighWater, status: _deriveStatus(withHighWater) };
}

function _dropApiRetryNotices(state: AgentPanelState): AgentPanelState {
  if (!state.notices.some((notice) => notice.type === 'api_retry')) return state;
  return {
    ...state,
    notices: state.notices.filter((notice) => notice.type !== 'api_retry'),
  };
}

/**
 * Recompute :attr:`AgentPanelState.contextTokensHighWater` after each
 * frame.
 *
 * **Only PER-ITERATION usage sources are considered.** A Claude Code
 * "turn" can be a chain of API calls (tool use → tool result → next
 * assistant call → ...), each with its own Anthropic message and its
 * own ``input + cache_creation + cache_read`` count. The SDK's
 * ``ResultMessage.usage`` aggregates these by summing fields across
 * iterations — and because each iteration re-reads ~the same
 * cached prefix, summing ``cache_read_input_tokens`` double-counts
 * the same tokens N times. A 5-iteration turn with a 200k cached
 * prefix can report a 1M+ aggregate cache read, which has produced
 * ">100% context full" readings in the chip. We deliberately ignore
 * that aggregate here and only consider per-iteration sources:
 *
 *   - ``message_start.usage`` on a stream_event — the per-iteration
 *     count Anthropic emits at the start of each Anthropic message.
 *   - the latest assistant turn's ``usage`` — captured from
 *     ``message_start`` and merged with deltas (also per-iteration).
 *
 * Reset (NOT max) on ``system.compact_boundary`` because that's the
 * one event that legitimately shrinks the conversation.
 */
function _updateContextHighWater(state: AgentPanelState, frame: OutboundFrame): AgentPanelState {
  if (frame.type === 'system.compact_boundary') {
    return state.contextTokensHighWater === 0 ? state : { ...state, contextTokensHighWater: 0 };
  }
  const candidates: number[] = [state.contextTokensHighWater];
  if (frame.type === 'stream_event') {
    const event = (frame as { event?: { type?: string; message?: { usage?: SseUsage }; usage?: SseUsage } }).event;
    // ``message_start`` carries the per-iteration usage. The SDK
    // emits one ``message_start`` per Anthropic API call within
    // a Claude Code turn, so this is the cleanest per-iteration
    // signal available.
    if (event?.type === 'message_start' && event?.message?.usage) {
      candidates.push(_usageContextSum(event.message.usage));
    }
  }
  // Latest committed assistant turn — its usage was set from
  // ``message_start`` (per-iteration) and only merged with output
  // deltas after that. Per-iteration too.
  //
  // Skip turns whose ``compactEpoch`` is older than the current
  // session epoch: their ``usage`` was captured against the
  // pre-compaction context window, and reading it here would
  // re-assert the giant pre-compact total on the very next
  // non-compact frame after a ``/compact``. Treat absent epoch as 0
  // so history-replayed turns from sessions older than this code
  // (or hand-built test fixtures) still contribute when no
  // compaction has happened.
  for (let i = state.turns.length - 1; i >= 0; i--) {
    const t = state.turns[i];
    if (t.role !== 'assistant' || !t.usage) continue;
    if ((t.compactEpoch ?? 0) < state.compactEpoch) continue;
    candidates.push(_usageContextSum(t.usage));
    break;
  }
  // NB: deliberately NOT reading from ``frame.usage`` for ``result``
  // frames or from ``state.lastResult.usage`` — those aggregate
  // across iterations and inflate the context fill (see the
  // function docstring for the full reasoning).
  const next = Math.max(...candidates);
  if (next === state.contextTokensHighWater) return state;
  return { ...state, contextTokensHighWater: next };
}

function _usageContextSum(usage: SseUsage): number {
  return (usage.input_tokens ?? 0) + (usage.cache_creation_input_tokens ?? 0) + (usage.cache_read_input_tokens ?? 0);
}

/**
 * Whether the agent ended a slash-style turn without producing any
 * response. Drives an inline hint in :class:`TurnStream` so the user
 * doesn't end up staring at a bare ``Worked for 0s`` after typing,
 * say, ``/goal something`` (when ``/goal`` isn't a defined command):
 * the CLI silently rejects the unknown slash, the result frame
 * arrives with zero cost / zero content, and -- without this --
 * the chat shows no trail of what happened.
 *
 * Conservative on purpose:
 *   - only fires when the tail turn is a committed user message,
 *   - only when the agent is fully idle (no active stream, no
 *     awaiting flag, no live turn-work),
 *   - only when the user's text starts with ``/`` (so a legitimate
 *     "you asked me to stay quiet" exchange never shows this),
 *   - only when no other system notice has been anchored to the
 *     same user turn (so ``/compact`` -- which is its own well-
 *     surfaced flow -- doesn't double up),
 *   - only when the slash command is NOT in the server's
 *     ``state.slashCommands`` list (built-ins like ``/compact``,
 *     ``/clear``, ``/help`` AND project commands under
 *     ``.claude/commands/``). The hint's whole point is to
 *     explain unknown slashes; emitting it for a recognized
 *     command incorrectly tells the user to "go define it",
 *   - and never while a manual ``/compact`` is mid-flight
 *     (``isCompactPending``) -- the "Compacting..." indicator is
 *     the truthful UX during that window, even before the
 *     ``compact_boundary`` notice anchors to the user turn.
 */
export function shouldShowNoResponseHint(state: AgentPanelState): boolean {
  if (state.awaitingAssistant) return false;
  if (state.activeStreamTurnId !== null) return false;
  if (state.status !== 'idle') return false;
  if (isInterruptPending(state)) return false;
  if (isCompactPending(state)) return false;
  const last = state.turns[state.turns.length - 1];
  if (last?.role !== 'user' || !last.committed) return false;
  if (state.notices.some((notice) => notice.anchorTurnId === last.id && notice.type !== 'init')) {
    return false;
  }
  const text = _userTurnText(last);
  const trimmed = text?.trim();
  if (!trimmed?.startsWith('/')) return false;
  // Extract just the command word (``/compact something`` -> ``compact``)
  // and check against the server-published slash command list. The
  // server pushes both built-ins and ``.claude/commands/`` entries,
  // so any recognized slash takes its own UX path and the hint
  // would be a false alarm.
  const commandName = trimmed.slice(1).split(/\s/, 1)[0]?.toLowerCase();
  if (commandName && state.slashCommands.some((known) => known.toLowerCase() === commandName)) {
    return false;
  }
  return true;
}

function _userTurnText(turn: Turn): string {
  for (const index of turn.order) {
    const block = turn.blocks[index];
    if (block?.block_type === 'TextBlock' && typeof block.text === 'string') return block.text;
  }
  return '';
}

/**
 * Per-iteration prompt size of the *latest* assistant turn we've
 * observed. This is what Claude Code's ``/context`` shows and what
 * users mean when they ask "how full is my context right now?".
 *
 * Different from :attr:`AgentPanelState.contextTokensHighWater`,
 * which is the **max** ever seen since the last compact and never
 * decreases. The high water is the safer over-estimate; the latest
 * is the up-to-the-minute reading -- it'll move down briefly during
 * multi-iteration sub-calls with smaller cache profiles, and that's
 * actually informative.
 *
 * Skips turns whose ``compactEpoch`` predates the current session
 * epoch for the same reason ``_updateContextHighWater`` does:
 * pre-compact usage no longer represents anything that's still in
 * the model's context.
 *
 * Returns ``null`` when no assistant turn yet carries a usage object
 * (fresh attach, just after compact, etc.) so callers can fall back
 * to a non-display rather than rendering "0 used".
 */
export function latestContextTokens(state: AgentPanelState): number | null {
  for (let i = state.turns.length - 1; i >= 0; i--) {
    const t = state.turns[i];
    if (t.role !== 'assistant' || !t.usage) continue;
    if ((t.compactEpoch ?? 0) < state.compactEpoch) continue;
    return _usageContextSum(t.usage);
  }
  return null;
}

function _reduce(state: AgentPanelState, frame: OutboundFrame): AgentPanelState {
  switch (frame.type) {
    case 'session_attached':
      return _onSessionAttached(state, frame);
    case 'system.init':
      return _onSystemInit(state, frame);
    case 'system.compact_boundary':
      return _onSystemCompactBoundary(state, frame);
    case 'system.api_retry':
      return _onSystemNotice(state, 'api_retry', frame);
    case 'assistant':
      return _onAssistant(state, frame);
    case 'user':
      return _onUser(state, frame);
    case 'stream_event':
      return _onStreamEvent(state, frame.event as unknown as { type: string; [k: string]: unknown });
    case 'task_started':
      return _onTaskStarted(state, frame);
    case 'task_progress':
      return _onTaskProgress(state, frame);
    case 'task_notification':
      return _onTaskNotification(state, frame);
    case 'rate_limit':
      return { ...state, rateLimit: frame.rate_limit_info };
    case 'interaction_request':
      return _onInteractionRequest(state, frame);
    case 'interaction_state':
      return _onInteractionState(state, frame);
    case 'interaction_response':
      return _onInteractionResponse(state, frame);
    case 'permission_mode_changed':
      return { ...state, permissionMode: frame.permission_mode };
    case 'result':
      return _onResult(state, frame);
    case 'server_error':
      return _onServerError(state, frame);
    case 'attachment_role_changed':
      // Role lives on the runtime snapshot, not the reducer state,
      // because it's a per-attachment fact rather than per-session.
      // The runtime intercepts these frames in ``_handleAttachEnvelope``-
      // adjacent code; the reducer is a no-op so the dispatcher
      // exhaustivity check stays clean.
      return state;
    case 'user_input_accepted':
      // Delivery acks are a browser-transport concern. The runtime
      // consumes them to clear pending drafts; reducer state changes
      // only when the accepted prompt is inserted optimistically.
      return state;
    default:
      return state;
  }
}

function _onInteractionRequest(state: AgentPanelState, frame: InteractionRequestFrame): AgentPanelState {
  const request = _interactionFromFrame(frame);
  const existing = state.pendingInteractions.findIndex((item) => item.request_id === request.request_id);
  const pendingInteractions =
    existing >= 0
      ? state.pendingInteractions.map((item, index) => (index === existing ? request : item))
      : [...state.pendingInteractions, request];
  return { ...state, pendingInteractions };
}

function _onInteractionState(state: AgentPanelState, frame: InteractionStateFrame): AgentPanelState {
  return { ...state, pendingInteractions: frame.pending.map(_normalizeInteractionRequest) };
}

function _onInteractionResponse(state: AgentPanelState, frame: InteractionResponseFrame): AgentPanelState {
  return applyOptimisticInteractionResponse(state, frame.request_id);
}

function _interactionFromFrame(frame: InteractionRequestFrame): InteractionRequest {
  const { type: _type, ...request } = frame;
  return _normalizeInteractionRequest(request);
}

function _normalizeInteractionRequest(request: InteractionRequest): InteractionRequest {
  return {
    request_id: request.request_id,
    tool_name: request.tool_name,
    tool_use_id: request.tool_use_id ?? null,
    kind: request.kind,
    input: request.input ?? {},
    created_at_ms: request.created_at_ms,
  };
}

function _onServerError(state: AgentPanelState, frame: ServerErrorFrame): AgentPanelState {
  // Safety net for in-flight streaming turns. A ``server_error`` can
  // arrive while an assistant turn is still streaming — most commonly
  // when the live-session pump crashes mid-thinking and emits the crash
  // frame without a preceding ``message_stop`` or ``result``. Commit
  // the open turn first so its thinking-block timing closes and
  // ``turn.committed`` flips to ``true``; otherwise ``AssistantStream``
  // keeps ``streaming = !turn.committed`` true and the live counter
  // ("Thinking for Ns") ticks forever until the user hits interrupt.
  // Mirrors the ``_onResult`` safety net below and ``applyInterrupt``'s
  // explicit ``committed: true``.
  const baseState = state.activeStreamTurnId !== null ? _commitStreamingTurn(state) : state;

  if (_serverErrorBelongsInTranscript(frame)) {
    const now = Date.now();
    const errorForUserText = _serverErrorUserText(frame);
    const errorTurn: Turn = {
      id: `server-error-${now}-${Math.random().toString(36).slice(2, 8)}`,
      role: 'assistant',
      blocks: {},
      order: [],
      partialToolInput: {},
      blockTimings: {},
      committed: true,
      startedAt: now,
      compactEpoch: baseState.compactEpoch,
      error: frame.error,
      errorForUserText,
    };
    const placed = _insertOrQueueAnchoredError(baseState.turns, baseState.pendingAnchoredErrors, errorTurn);
    return {
      ...baseState,
      turns: placed.turns,
      pendingAnchoredErrors: placed.pendingAnchoredErrors,
      awaitingAssistant: false,
      activeStreamTurnId: null,
      turnWork: emptyTurnWork(),
    };
  }

  // Fallback branch (infrastructure errors filtered out of the
  // transcript). If we just committed a streaming turn, finish the
  // cleanup so the working indicator and live counters stop —
  // ``_commitStreamingTurn`` set ``awaitingAssistant`` based on the
  // pre-error stop reason, which is stale once the session has
  // crashed.
  if (state.activeStreamTurnId !== null) {
    return {
      ...baseState,
      errors: [...baseState.errors, frame.error],
      awaitingAssistant: false,
      activeStreamTurnId: null,
      turnWork: emptyTurnWork(),
    };
  }

  return { ...state, errors: [...state.errors, frame.error] };
}

function _serverErrorBelongsInTranscript(frame: ServerErrorFrame): boolean {
  const message = frame.error.trim();
  if (!message) return false;
  if (/crash|session not started|read-only|websocket|connection|network|auth|token/i.test(message)) return false;
  const requestType = frame.frame?.type;
  return requestType === 'user_input' || /^\/\S+.*isn't available in this environment\.?$/i.test(message);
}

function _serverErrorUserText(frame: ServerErrorFrame): string | null {
  const text = frame.frame?.text;
  return typeof text === 'string' && text.trim() ? text.trim() : null;
}

function _insertOrQueueAnchoredError(
  turns: Turn[],
  pendingAnchoredErrors: Turn[],
  errorTurn: Turn
): { turns: Turn[]; pendingAnchoredErrors: Turn[] } {
  const inserted = _insertErrorTurnAfterMatchingUser(turns, errorTurn);
  if (inserted) {
    return {
      turns: inserted,
      pendingAnchoredErrors: _dropEquivalentAnchoredError(pendingAnchoredErrors, errorTurn),
    };
  }

  if (pendingAnchoredErrors.some((pending) => _sameAnchoredError(pending, errorTurn))) {
    return { turns, pendingAnchoredErrors };
  }
  return { turns, pendingAnchoredErrors: [...pendingAnchoredErrors, errorTurn] };
}

function _flushPendingAnchoredErrors(
  turns: Turn[],
  pendingAnchoredErrors: Turn[]
): { turns: Turn[]; pendingAnchoredErrors: Turn[] } {
  let nextTurns = turns;
  const stillPending: Turn[] = [];
  for (const pending of pendingAnchoredErrors) {
    const inserted = _insertErrorTurnAfterMatchingUser(nextTurns, pending);
    if (inserted) nextTurns = inserted;
    else if (!stillPending.some((existing) => _sameAnchoredError(existing, pending))) stillPending.push(pending);
  }
  return { turns: nextTurns, pendingAnchoredErrors: stillPending };
}

function _insertErrorTurnAfterMatchingUser(turns: Turn[], errorTurn: Turn): Turn[] | null {
  const anchor = _normalizeErrorAnchorText(errorTurn.errorForUserText);
  if (!anchor) return [...turns, errorTurn];

  let targetIndex = -1;
  for (let i = turns.length - 1; i >= 0; i--) {
    const turn = turns[i];
    if (turn.role !== 'user') continue;
    if (_normalizeErrorAnchorText(_turnPlainText(turn)) !== anchor) continue;
    targetIndex = i;
    break;
  }
  if (targetIndex < 0) return null;

  for (let i = targetIndex + 1; i < turns.length; i++) {
    const turn = turns[i];
    if (turn.role === 'user') break;
    if (turn.role === 'assistant' && turn.error === errorTurn.error) return turns;
  }

  return [...turns.slice(0, targetIndex + 1), errorTurn, ...turns.slice(targetIndex + 1)];
}

function _dropEquivalentAnchoredError(errors: Turn[], target: Turn): Turn[] {
  return errors.filter((error) => !_sameAnchoredError(error, target));
}

function _sameAnchoredError(a: Turn, b: Turn): boolean {
  return (
    a.error === b.error &&
    _normalizeErrorAnchorText(a.errorForUserText) === _normalizeErrorAnchorText(b.errorForUserText)
  );
}

function _normalizeErrorAnchorText(text: string | null | undefined): string | null {
  const trimmed = text?.trim();
  return trimmed ? trimmed : null;
}

// =============================================================================
// Session / system
// =============================================================================

function _onSessionAttached(state: AgentPanelState, frame: SessionAttachedFrame): AgentPanelState {
  const slashCommands = _normalizeSlashCommands(frame.slash_commands);
  const next: AgentPanelState = {
    ...state,
    attached: true,
    cwd: frame.cwd,
    sessionId: frame.resume ?? state.sessionId,
    // First-paint model hint from the attach envelope. The SDK's own
    // ``system.init.data.model`` event remains authoritative and
    // overwrites this when it arrives.
    model: frame.model ?? state.model,
    effort: frame.effort ?? state.effort,
    speed: frame.speed ?? state.speed,
    permissionMode: frame.permission_mode ?? state.permissionMode,
    protocolVersion: typeof frame.protocol_version === 'number' ? frame.protocol_version : state.protocolVersion,
    capabilities: Array.isArray(frame.capabilities)
      ? frame.capabilities.filter((capability): capability is string => typeof capability === 'string')
      : state.capabilities,
    slashCommands: slashCommands.length > 0 ? slashCommands : state.slashCommands,
  };
  return next;
}

function _onSystemInit(state: AgentPanelState, frame: SystemInitFrame): AgentPanelState {
  const data = frame.data ?? {};
  const next: AgentPanelState = {
    ...state,
    notices: [...state.notices, { id: `notice-${state.notices.length}`, type: 'init', data, at: Date.now() }],
  };
  if (typeof data.model === 'string') next.model = data.model;
  if (typeof data.session_id === 'string') next.sessionId = data.session_id;
  if (typeof data.cwd === 'string') next.cwd = data.cwd;
  const slashCommands = _normalizeSlashCommands(data.slash_commands);
  if (slashCommands.length > 0) next.slashCommands = slashCommands;
  return next;
}

function _normalizeSlashCommands(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  const commands: string[] = [];
  for (const item of value) {
    if (typeof item !== 'string') continue;
    const command = item.replace(/^\/+/, '').trim();
    if (!command || seen.has(command)) continue;
    seen.add(command);
    commands.push(command);
  }
  return commands;
}

function _onSystemNotice(
  state: AgentPanelState,
  kind: 'compact_boundary' | 'api_retry',
  frame: SystemCompactBoundaryFrame | SystemApiRetryFrame
): AgentPanelState {
  const anchorTurnId = state.turns[state.turns.length - 1]?.id ?? null;
  const notice: SystemNotice = {
    id: `notice-${state.notices.length}`,
    type: kind,
    data: frame.data ?? {},
    at: Date.now(),
    anchorTurnId,
  };
  if (kind === 'api_retry' && state.notices[state.notices.length - 1]?.type === 'api_retry') {
    return {
      ...state,
      notices: [...state.notices.slice(0, -1), { ...notice, id: state.notices[state.notices.length - 1].id }],
    };
  }

  return {
    ...state,
    notices: [...state.notices, notice],
  };
}

// Matches the bare ``/compact`` slash command that an optimistic user
// turn carries when the user invokes manual compaction from the
// composer (and the same text the server echoes back as the committed
// user frame). Used to suppress the awaits-assistant flag on that
// turn once the boundary lands: the CLI handles ``/compact``
// synchronously and emits no ``ResultMessage``, so without this gate
// ``_submittedUserAwaitsAssistant`` keeps reporting ``true`` for the
// ``/compact`` bubble and the Working indicator never clears.
const COMPACT_COMMAND_RE = /^\s*\/compact(\s|$)/;

function _onSystemCompactBoundary(state: AgentPanelState, frame: SystemCompactBoundaryFrame): AgentPanelState {
  const noticed = _onSystemNotice(state, 'compact_boundary', frame);
  // Bump the compact epoch on EVERY boundary (manual or auto) so any
  // assistant turn whose ``usage`` predates this point is excluded
  // from the high-water walk in ``_updateContextHighWater``. Clear
  // the optimistic ``pendingCompactSince`` indicator too -- it served
  // its purpose the moment the boundary actually landed.
  const withEpoch: AgentPanelState = {
    ...noticed,
    compactEpoch: noticed.compactEpoch + 1,
    pendingCompactSince: null,
  };
  if (_compactBoundaryTrigger(frame) !== 'manual') return withEpoch;

  // Manual ``/compact`` is terminated by this very frame -- no
  // ``ResultMessage`` follows, no terminal ``stop_reason`` lands on
  // any in-flight assistant turn -- so the normal completion path
  // never fires. Clear the two activity sources that would otherwise
  // hold the Working indicator open:
  //   1. The optimistic ``/compact`` user turn's ``awaitsAssistant``
  //      flag (so ``_submittedUserAwaitsAssistant`` only considers
  //      genuinely-pending follow-up prompts).
  //   2. ``activity.activeTasks`` (any Task/Bash subagent that was
  //      mid-flight when the user typed ``/compact`` -- the CLI
  //      tears them down as part of the compaction handshake and
  //      we will never see their ``task_notification`` close-out).
  const turnsWithoutCompactWait = _markCompactCommandSettled(withEpoch.turns);
  const activeTasksChanged = Object.keys(withEpoch.activity.activeTasks).length > 0;
  const clearedActivity = activeTasksChanged
    ? _withRecountedActivity({ ...withEpoch.activity, activeTasks: {} })
    : withEpoch.activity;
  const cleared: AgentPanelState = {
    ...withEpoch,
    turns: turnsWithoutCompactWait,
    activity: clearedActivity,
  };
  const baseState = cleared.activeStreamTurnId !== null ? _commitStreamingTurn(cleared) : cleared;
  const hasSubmittedTail = _submittedUserAwaitsAssistant(baseState);
  return {
    ...baseState,
    awaitingAssistant: hasSubmittedTail,
    activeStreamTurnId: null,
    turnWork: _turnWorkForCompactBoundary(baseState.turnWork, frame, hasSubmittedTail),
  };
}

/**
 * Clear ``awaitsAssistant`` on every ``/compact`` user turn that is
 * still waiting on a response.
 *
 * Originally this was gated on ``optimistic === true`` on the theory
 * that the not-yet-echoed bubble was the only one that could keep
 * the working indicator latched. In practice the server's user-frame
 * echo for ``/compact`` races the ``system.compact_boundary`` frame
 * and frequently arrives first -- ``_onUser`` reconciles the turn
 * to ``optimistic: false`` but preserves ``awaitsAssistant: true``,
 * so the gate dropped exactly the case the helper exists to fix.
 *
 * Manual ``/compact`` never receives a real ``ResultMessage``, so
 * any ``/compact`` user turn whose matching boundary has now landed
 * cannot legitimately still be awaiting an assistant reply.
 * Clearing the flag unconditionally for matching turns covers both
 * the pre-echo and post-echo states without re-introducing the
 * "Working" stuck-state regression.
 */
function _markCompactCommandSettled(turns: Turn[]): Turn[] {
  let changed = false;
  const next = turns.map((turn) => {
    if (turn.role !== 'user') return turn;
    if (turn.awaitsAssistant === false) return turn;
    if (!COMPACT_COMMAND_RE.test(_turnPlainText(turn))) return turn;
    changed = true;
    return { ...turn, awaitsAssistant: false };
  });
  return changed ? next : turns;
}

function _compactBoundaryTrigger(frame: SystemCompactBoundaryFrame): string | null {
  const data = frame.data ?? {};
  if (typeof data.trigger === 'string') return data.trigger;

  const metadata = data.compactMetadata;
  if (metadata !== null && typeof metadata === 'object' && !Array.isArray(metadata)) {
    const trigger = (metadata as { trigger?: unknown }).trigger;
    if (typeof trigger === 'string') return trigger;
  }
  return null;
}

function _turnWorkForCompactBoundary(
  turnWork: AgentTurnWorkState,
  frame: SystemCompactBoundaryFrame,
  keepsTurnActive: boolean
): AgentTurnWorkState {
  if (keepsTurnActive) {
    return { ...turnWork, completedDurationMs: null };
  }

  const durationMs =
    _validDurationMs(frame.last_completed_turn_duration_ms) ??
    (turnWork.startedAt !== null ? Math.max(0, Date.now() - turnWork.startedAt) : null);
  return {
    startedAt: null,
    completedDurationMs: durationMs ?? turnWork.completedDurationMs,
  };
}

// =============================================================================
// Assistant / user frames (post-streamed, committed)
// =============================================================================

function _onAssistant(state: AgentPanelState, frame: AssistantFrame): AgentPanelState {
  // Subagent internals can arrive as assistant-role sidechain
  // messages keyed by the parent Task tool use. The parent chat
  // already represents that work through the Task row + task_*
  // frames, so rendering the child transcript as top-level turns
  // makes the agent look like it is talking to itself.
  if (frame.parent_tool_use_id) return state;

  const turnId = frame.message_id ?? frame.uuid ?? `assistant-${state.turns.length}`;
  const existingIdx = state.turns.findIndex((t) => t.id === turnId || t.messageId === frame.message_id);
  const content = frame.content ?? [];
  const syntheticError = _syntheticAssistantError(frame, content);
  if (_isNoResponseRequested(syntheticError)) return _clearNoResponseRequested(state);
  const errorForUserText = _syntheticErrorUserText(syntheticError);
  const visibleContent = syntheticError ? [] : content;
  const blocks = _blocksToIndexed(visibleContent);
  const existingTurn = existingIdx >= 0 ? state.turns[existingIdx] : null;

  // Intermediate per-block emission during live streaming. The Claude
  // Agent SDK emits one ``assistant`` frame per completed content
  // block in a multi-block turn — so a thinking + text reply ships
  // *two* assistant frames, one after the thinking block closes and a
  // second after the text block closes, with ``stop_reason: null`` on
  // both. The final commit signal is ``message_stop``, not these
  // intermediate per-block frames.
  //
  // Without this guard, the first intermediate frame would commit the
  // turn and null out ``activeStreamTurnId``; every subsequent
  // ``content_block_delta`` (text_delta) would be silently dropped by
  // ``_updateActiveStreamTurn`` and the user would see the thinking
  // block, then a long stall, then the entire reply dropped at once
  // when the second assistant frame's canonical content overlays the
  // committed turn. Interrupting mid-paragraph emits an assistant
  // frame with whatever the SDK had buffered — that's the "interrupt
  // shows half the paragraph" symptom.
  //
  // Discriminator: real SDK intermediate frames carry
  // ``stop_reason: null``. When the SDK *does* stamp a terminal
  // ``stop_reason`` on the AssistantMessage itself (synthetic test
  // fixtures, some tool_use paths), commit immediately so the
  // awaitingAssistant + tool-loop logic latches the right state.
  const isLiveStreaming =
    existingTurn !== null &&
    !existingTurn.committed &&
    !syntheticError &&
    frame.stop_reason == null &&
    (state.activeStreamTurnId === existingTurn.id || state.activeStreamTurnId === existingTurn.messageId);

  if (isLiveStreaming && existingTurn !== null) {
    // Streaming SSE events are the authoritative source of block
    // content for an in-flight turn — everything the per-block
    // intermediate assistant frame carries was already delivered via
    // ``content_block_start`` / ``content_block_delta`` /
    // ``content_block_stop``. We deliberately do NOT merge block
    // content here: the SDK lays out the AssistantMessage's content
    // array at zero-based positions (``0`` for whichever single block
    // just closed), while the streaming state keys on Anthropic SSE
    // ``content_block.index`` (``0`` for thinking, ``1`` for the text
    // that follows it). Merging on raw position would create a
    // phantom duplicate of the text block at a fresh index.
    //
    // Pick up only the frame-level metadata that streaming events
    // don't carry — usage adjustments, model identity, the rare
    // case where the SDK does stamp a stop_reason on the
    // AssistantMessage itself — and leave block contents alone.
    const turns = state.turns.slice();
    turns[existingIdx] = {
      ...existingTurn,
      model: frame.model ?? existingTurn.model,
      usage: _mergeUsage(existingTurn.usage, frame.usage),
      stopReason: frame.stop_reason ?? existingTurn.stopReason,
    };
    return { ...state, turns };
  }

  const committedAt = Date.now();
  const committedBlocks = existingTurn?.committed
    ? _mergeCommittedAssistantBlocks(existingTurn, blocks)
    : existingTurn
      ? _mergeStreamingAssistantBlocks(existingTurn, blocks)
      : blocks;
  const committedTurn: Turn = {
    id: turnId,
    role: 'assistant',
    blocks: committedBlocks.blocks,
    order: committedBlocks.order,
    partialToolInput: {},
    // Preserve any timings the streaming reducer captured so the
    // shimmer→committed swap doesn't blank out a thinking duration
    // the user already saw ticking up.
    blockTimings: existingTurn ? _closeOpenThinkingTimings(existingTurn, committedAt) : {},
    model: frame.model,
    messageId: frame.message_id,
    // Merge into any prior streamed usage rather than replacing it.
    // The SDK's finalized assistant frame can ship a usage object
    // missing the cache fields (or any input field) depending on
    // whether it was reconstructed from JSONL vs. live; preserving
    // values streamed in via ``message_start`` keeps the context
    // chip stable across the streaming → committed transition.
    usage: _mergeUsage(existingTurn?.usage, frame.usage),
    stopReason: frame.stop_reason ?? null,
    committed: true,
    startedAt: existingIdx >= 0 ? state.turns[existingIdx].startedAt : committedAt,
    // Preserve any epoch the streaming turn already carried so a
    // commit that lands after a compaction boundary does not
    // accidentally promote a pre-compact partial into the current
    // epoch. Default to the live epoch for brand-new commits.
    compactEpoch: existingTurn?.compactEpoch ?? state.compactEpoch,
    error: frame.error ?? syntheticError ?? null,
    errorForUserText,
  };
  // When an assistant turn ends with ``stop_reason: "tool_use"`` the
  // model has called tools and is waiting on their results — the agent
  // is *still working*, the user just shouldn't see the bubble go
  // quiet. Flip ``awaitingAssistant`` back on so the bouncing-dot
  // indicator persists across the gap between this committed turn and
  // the next ``message_start``. The next turn's ``message_start``
  // clears it normally.
  //
  // Critically: only re-arm the flag for *live* commits — turns that
  // are brand-new or finalizing an in-flight uncommitted turn. A
  // re-emission of an already-committed turn (the JSONL replay the
  // SDK fires on session resume) carries the same ``tool_use``
  // stop_reason as the original commit, but no SDK is actively
  // working on it; treating it as live wedges the chat-tail shimmer
  // at "Thinking…" forever for any session whose transcript ends
  // with a tool_use that never got its tool_result (a wedged tool
  // call, an interrupted run, an `AskUserQuestion` we no longer let
  // the model call, …).
  const stoppedForTool = _assistantFrameAwaitsTool(visibleContent, frame.stop_reason);
  const isReEmission = existingIdx >= 0 && state.turns[existingIdx].committed;
  const addsNewContent =
    existingTurn !== null && visibleContent.some((block) => !_turnHasEquivalentBlock(existingTurn, block));
  const nextAwaiting = isReEmission && !addsNewContent ? state.awaitingAssistant : stoppedForTool;
  let next: AgentPanelState;
  if (existingIdx >= 0) {
    const turns = state.turns.slice();
    turns[existingIdx] = committedTurn;
    next = { ...state, turns, awaitingAssistant: nextAwaiting, activeStreamTurnId: null };
  } else if (errorForUserText) {
    const placed = _insertOrQueueAnchoredError(state.turns, state.pendingAnchoredErrors, committedTurn);
    next = {
      ...state,
      turns: placed.turns,
      pendingAnchoredErrors: placed.pendingAnchoredErrors,
      awaitingAssistant: nextAwaiting,
      activeStreamTurnId: null,
    };
  } else {
    next = {
      ...state,
      turns: [...state.turns, committedTurn],
      awaitingAssistant: nextAwaiting,
      activeStreamTurnId: null,
    };
  }
  // Funnel every committed tool_use through the activity processor —
  // this is the source of truth for the registry and any plan / plan
  // proposal updates, since the SDK can deliver an ``assistant`` frame
  // with full tool inputs even when the streaming SSE path was skipped
  // (e.g. when resuming a session, the JSONL replay only ships
  // committed frames).
  for (const block of visibleContent) {
    if (block.block_type === 'ToolUseBlock' || block.block_type === 'ServerToolUseBlock') {
      next = _processToolUseBlock(next, block as ToolUseBlock);
    }
  }
  return _completeTurnWorkAfterTerminalAssistant(next, frame.stop_reason);
}

function _clearNoResponseRequested(state: AgentPanelState): AgentPanelState {
  if (!state.awaitingAssistant) return state;
  const next: AgentPanelState = {
    ...state,
    awaitingAssistant: false,
  };
  return { ...next, status: _deriveStatus(next) };
}

function _syntheticErrorUserText(error: string | null): string | null {
  const match = error?.trim().match(/^(\/\S+)\s+isn't available in this environment\.?$/i);
  return match?.[1] ?? null;
}

function _assistantFrameAwaitsTool(
  content: ContentBlock[],
  stopReason: StopReason | string | null | undefined
): boolean {
  if (stopReason === 'tool_use') return true;
  if (stopReason !== null && stopReason !== undefined) return false;

  // Older / replayed frames may omit stop_reason. In that case, a
  // tool_use is only "waiting" when no final prose follows it.
  let lastToolIndex = -1;
  let lastTextIndex = -1;
  for (let index = 0; index < content.length; index++) {
    const block = content[index];
    if (block.block_type === 'ToolUseBlock' || block.block_type === 'ServerToolUseBlock') {
      lastToolIndex = index;
    } else if (block.block_type === 'TextBlock' && block.text.trim().length > 0) {
      lastTextIndex = index;
    }
  }
  return lastToolIndex >= 0 && lastTextIndex < lastToolIndex;
}

function _onUser(state: AgentPanelState, frame: UserFrame): AgentPanelState {
  const isToolResultEcho = frame.tool_use_result != null;
  const blocks: ContentBlock[] = _normalizeUserContent(frame.content);

  // Claude's wire format uses user-role messages for more than human
  // input. Tool-result echoes are handled below, but child-agent
  // prompts can also show up as user messages with ``parent_tool_use_id``.
  // Those are model-side context for a subagent, not text the human sent.
  if (!isToolResultEcho && frame.parent_tool_use_id) return state;

  // Skill / SlashCommand body echo. When the agent invokes one of
  // these tools, Claude Code resolves the skill or command file
  // and injects its full markdown body as a synthetic UserMessage
  // so the model can reason against it on the next turn. The
  // JSONL flags this with ``isMeta: true`` but the SDK strips that
  // marker before forwarding, leaving us to detect the echo by
  // context: a plain text-only UserMessage that arrives
  // immediately after an assistant turn whose final tool call was
  // ``Skill`` / ``SlashCommand`` is the body, not human input.
  // We hide it from the chat — the tool row already shows
  // ``Skill simplify`` and the body lives behind its dropdown.
  // Most slash-command echoes are caught upstream by
  // ``is_synthetic_envelope`` on the python side (``# /<word>``);
  // this guard catches Skill-tool echoes whose body just starts
  // with an arbitrary ``# <Title>`` heading and can't be
  // discriminated by content shape alone.
  if (!isToolResultEcho && _isSkillBodyEcho(state, blocks)) return state;

  // Route tool_result blocks to the side-channel map so the UI can inline them
  // under their matching ToolUseBlock.
  if (isToolResultEcho) {
    let toolResults = state.toolResults;
    let updated = false;
    const resultBlocks: ToolResultBlock[] = [];
    for (const block of blocks) {
      if (block.block_type === 'ToolResultBlock') {
        if (!updated) {
          toolResults = { ...toolResults };
          updated = true;
        }
        toolResults[block.tool_use_id] = block;
        resultBlocks.push(block);
      }
    }
    if (!updated) return state;
    const isLiveToolLoop = state.awaitingAssistant || hasUnresolvedCommittedTool(state);
    let next = { ...state, toolResults, awaitingAssistant: isLiveToolLoop };
    for (const block of resultBlocks) {
      next = _processToolResultBlock(next, block);
    }
    return { ...next, status: _deriveStatus(next) };
  }

  const indexed = _blocksToIndexed(blocks);

  // Primary reconciliation path: the server stamps every webapp-submitted
  // user frame with the client_message_id it received in ``user_input``,
  // so we can replace the optimistic bubble in place by id. No text
  // matching, no timestamp windows, no FIFO heuristic.
  if (frame.client_message_id) {
    const idx = state.turns.findIndex(
      (turn) => turn.role === 'user' && turn.clientMessageId === frame.client_message_id
    );
    if (idx >= 0) {
      const turns = state.turns.slice();
      const prev = turns[idx];
      turns[idx] = {
        ...prev,
        messageId: frame.uuid ?? prev.messageId,
        blocks: indexed.blocks,
        order: indexed.order,
        committed: true,
        optimistic: false,
        clientMessageId: frame.client_message_id ?? prev.clientMessageId,
      };
      return { ...state, turns };
    }
  }

  // Idempotent on server uuid: a late echo (replay after reconnect) or
  // history rehydrate finds the row it already wrote and refreshes it
  // instead of appending a duplicate.
  if (frame.uuid) {
    const existingIdx = state.turns.findIndex(
      (turn) => turn.role === 'user' && (turn.id === frame.uuid || turn.messageId === frame.uuid)
    );
    if (existingIdx >= 0) {
      const turns = state.turns.slice();
      const prev = turns[existingIdx];
      turns[existingIdx] = {
        ...prev,
        messageId: frame.uuid,
        blocks: indexed.blocks,
        order: indexed.order,
        committed: true,
        optimistic: false,
        clientMessageId: frame.client_message_id ?? prev.clientMessageId,
      };
      return { ...state, turns };
    }
  }

  // Defensive FIFO fallback for the rare case where the server did
  // not (or could not) stamp ``client_message_id`` onto the echoed
  // user frame — older server, dropped queue, or the prompt happened
  // to match an earlier identical-text submission whose id had
  // already been consumed. Reconcile against the oldest pending
  // optimistic user so we never end up with an orphan placeholder
  // sitting above a duplicate committed bubble.
  const optimisticIdx = state.turns.findIndex((t) => t.role === 'user' && t.optimistic === true);
  if (optimisticIdx >= 0) {
    const turns = state.turns.slice();
    const prev = turns[optimisticIdx];
    turns[optimisticIdx] = {
      ...prev,
      messageId: frame.uuid ?? prev.messageId,
      blocks: indexed.blocks,
      order: indexed.order,
      committed: true,
      optimistic: false,
      clientMessageId: frame.client_message_id ?? prev.clientMessageId,
    };
    return { ...state, turns };
  }

  const turnId = frame.uuid ?? `user-${state.turns.length}-${Date.now()}`;
  const userTurn: Turn = {
    id: turnId,
    role: 'user',
    blocks: indexed.blocks,
    order: indexed.order,
    partialToolInput: {},
    blockTimings: {},
    messageId: frame.uuid ?? undefined,
    committed: true,
    clientMessageId: frame.client_message_id,
    startedAt: Date.now(),
  };
  return { ...state, turns: [...state.turns, userTurn] };
}

// Tools whose results are re-injected as synthetic ``isMeta`` user
// messages by Claude Code so the model can read the resolved body
// on its next turn. The SDK forwards both the tool_result *and* the
// echo without distinguishing them — we drop the echo so it doesn't
// render as a giant grey card under the user's prompt.
const _SKILL_ECHO_TOOL_NAMES = new Set(['Skill', 'SkillTool', 'SlashCommand']);

function _isSkillBodyEcho(state: AgentPanelState, blocks: ContentBlock[]): boolean {
  // Echo is always a plain text payload — never carries images,
  // documents, or tool_result blocks.
  if (blocks.length === 0) return false;
  if (!blocks.every((b) => b.block_type === 'TextBlock')) return false;

  // The triggering tool use lives on the most recent assistant
  // turn. We scan the latest turn's blocks (newest first) for a
  // ToolUseBlock whose name matches a known skill-echo source.
  for (let i = state.turns.length - 1; i >= 0; i--) {
    const turn = state.turns[i];
    if (turn.role !== 'assistant') continue;
    for (let j = turn.order.length - 1; j >= 0; j--) {
      const block = turn.blocks[turn.order[j]];
      if (block?.block_type === 'ToolUseBlock' && _SKILL_ECHO_TOOL_NAMES.has(block.name)) {
        return true;
      }
    }
    // Stop at the first assistant turn — any older tool use is
    // unrelated to this user message.
    return false;
  }
  return false;
}

function _normalizeUserContent(content: UserFrame['content']): ContentBlock[] {
  if (typeof content === 'string') {
    return [{ block_type: 'TextBlock', text: content, citations: null }];
  }
  if (Array.isArray(content)) return content;
  return [{ block_type: 'TextBlock', text: JSON.stringify(content), citations: null }];
}

// =============================================================================
// Raw SSE events (inside stream_event frames)
// =============================================================================

function _onStreamEvent(state: AgentPanelState, event: { type: string; [k: string]: unknown }): AgentPanelState {
  switch (event.type) {
    case 'message_start':
      return _onSseMessageStart(state, event as Record<string, unknown>);
    case 'content_block_start':
      return _onSseContentBlockStart(state, event as unknown as SseContentBlockStart);
    case 'content_block_delta':
      return _onSseContentBlockDelta(state, event as unknown as SseContentBlockDelta);
    case 'content_block_stop':
      return _onSseContentBlockStop(state, event as unknown as { type: 'content_block_stop'; index: number });
    case 'message_delta':
      return _onSseMessageDelta(state, event as unknown as SseMessageDelta);
    case 'message_stop':
      return _onSseMessageStop(state);
    case 'error':
      return {
        ...state,
        errors: [...state.errors, String((event as { error?: { message?: string } }).error?.message ?? 'stream error')],
      };
    default:
      return state;
  }
}

function _onSseMessageStart(state: AgentPanelState, event: Record<string, unknown>): AgentPanelState {
  const message = (event.message as { id?: string; model?: string; usage?: SseUsage } | undefined) ?? {};
  const id = message.id ?? `assistant-${state.turns.length}`;
  const activeTurn = state.activeStreamTurnId
    ? (state.turns.find(
        (turn) => turn.id === state.activeStreamTurnId || turn.messageId === state.activeStreamTurnId
      ) ?? null)
    : null;
  const incomingIsActive = activeTurn !== null && (activeTurn.id === id || activeTurn.messageId === id);
  // API retries can start a fresh Anthropic message without sending a
  // terminal frame for the failed one. Freeze the orphaned turn before
  // the active pointer moves, or its thinking block stays live forever.
  const baseState = state.activeStreamTurnId !== null && !incomingIsActive ? _commitStreamingTurn(state) : state;
  const existing = baseState.turns.find((t) => t.id === id || t.messageId === id);
  if (existing) {
    const next = _ensureTurnWorkStarted(baseState);
    const activeStreamTurnId = existing.committed ? null : existing.id;
    return next.awaitingAssistant || next.activeStreamTurnId !== activeStreamTurnId
      ? { ...next, awaitingAssistant: false, activeStreamTurnId }
      : next;
  }
  const newTurn: Turn = {
    id,
    role: 'assistant',
    blocks: {},
    order: [],
    partialToolInput: {},
    blockTimings: {},
    model: message.model,
    messageId: id,
    usage: message.usage,
    committed: false,
    startedAt: Date.now(),
    compactEpoch: baseState.compactEpoch,
  };
  const next = _ensureTurnWorkStarted(baseState);
  return { ...next, turns: [...next.turns, newTurn], awaitingAssistant: false, activeStreamTurnId: id };
}

function _ensureTurnWorkStarted(state: AgentPanelState): AgentPanelState {
  if (state.turnWork.startedAt !== null) return state;
  // Don't re-arm turn-work while an interrupt is pending: the user
  // already pressed Stop, the server hasn't observed the interrupt
  // yet, and any in-flight ``message_start`` would otherwise restart
  // the working timer and flash the Stop button back into existence.
  if (isInterruptPending(state)) return state;
  return {
    ...state,
    turnWork: {
      startedAt: Date.now(),
      completedDurationMs: null,
    },
  };
}

function _onSseContentBlockStart(state: AgentPanelState, event: SseContentBlockStart): AgentPanelState {
  const startedAt = Date.now();
  const next = _updateActiveStreamTurn(state, (turn) => {
    if (turn.committed) return turn;
    const skeleton = event.content_block;
    const seeded = _seedBlockFromSkeleton(skeleton);
    const blocks = { ...turn.blocks, [event.index]: seeded };
    const order = turn.order.includes(event.index) ? turn.order : [...turn.order, event.index];
    const closedTimings = _closeOpenThinkingTimings(turn, startedAt, event.index);
    const blockTimings = { ...closedTimings, [event.index]: { startedAt } };
    return { ...turn, blocks, order, blockTimings };
  });
  // Register the tool name immediately so any ``task_started`` that
  // arrives mid-stream — before the input json finishes — can still be
  // categorized correctly. The skeleton already carries ``id`` and
  // ``name`` for tool_use blocks; the input is filled in later at
  // ``content_block_stop``.
  const skeleton = event.content_block;
  if ((skeleton.type === 'tool_use' || skeleton.type === 'server_tool_use') && skeleton.id && skeleton.name) {
    return _registerToolUse(next, skeleton.id, skeleton.name);
  }
  return next;
}

function _seedBlockFromSkeleton(skeleton: SseContentBlockStart['content_block']): ContentBlock {
  switch (skeleton.type) {
    case 'text':
      return { block_type: 'TextBlock', text: skeleton.text ?? '', citations: skeleton.citations ?? null };
    case 'image':
      return { block_type: 'ImageBlock', source: skeleton.source, caption: skeleton.caption ?? null };
    case 'document':
      return {
        block_type: 'DocumentBlock',
        source: skeleton.source,
        title: skeleton.title ?? null,
        size: skeleton.size ?? null,
      };
    case 'thinking':
      return { block_type: 'ThinkingBlock', thinking: skeleton.thinking ?? '', signature: skeleton.signature ?? '' };
    case 'redacted_thinking':
      return { block_type: 'RedactedThinkingBlock', data: skeleton.data };
    case 'tool_use':
      return { block_type: 'ToolUseBlock', id: skeleton.id, name: skeleton.name, input: skeleton.input ?? {} };
    case 'server_tool_use':
      return { block_type: 'ServerToolUseBlock', id: skeleton.id, name: skeleton.name, input: skeleton.input ?? {} };
    case 'web_search_tool_result':
      return {
        block_type: 'WebSearchToolResultBlock',
        tool_use_id: skeleton.tool_use_id,
        content: skeleton.content ?? [],
      };
    case 'code_execution_tool_result':
      return {
        block_type: 'CodeExecutionToolResultBlock',
        tool_use_id: skeleton.tool_use_id,
        content: skeleton.content ?? {},
      };
  }
}

function _onSseContentBlockDelta(state: AgentPanelState, event: SseContentBlockDelta): AgentPanelState {
  const receivedAt = Date.now();
  return _updateActiveStreamTurn(state, (turn) => {
    if (turn.committed) return turn;
    const idx = event.index;
    const existing = turn.blocks[idx];
    if (!existing) {
      // Delta before start (shouldn't happen, but tolerate): seed as text.
      const seeded = _applyDelta(_seedEmptyFromDelta(event.delta), event.delta);
      const blocks = { ...turn.blocks, [idx]: seeded };
      const order = turn.order.includes(idx) ? turn.order : [...turn.order, idx];
      const closedTimings =
        seeded.block_type === 'ThinkingBlock' ? turn.blockTimings : _closeOpenThinkingTimings(turn, receivedAt, idx);
      const blockTimings =
        seeded.block_type === 'ThinkingBlock' ? { ...closedTimings, [idx]: { startedAt: receivedAt } } : closedTimings;
      return { ...turn, blocks, order, blockTimings, ...(_applyDeltaToPartialTool(turn, event) ?? {}) };
    }
    const mutated = _applyDelta(existing, event.delta);
    const partialPatch = _applyDeltaToPartialTool(turn, event);
    const blocks = { ...turn.blocks, [idx]: mutated };
    const blockTimings =
      event.delta.type === 'signature_delta' && existing.block_type === 'ThinkingBlock'
        ? _closeTimingAt(turn.blockTimings, idx, receivedAt)
        : turn.blockTimings;
    return { ...turn, blocks, blockTimings, ...(partialPatch ?? {}) };
  });
}

function _seedEmptyFromDelta(delta: SseContentBlockDelta['delta']): ContentBlock {
  if (delta.type === 'thinking_delta' || delta.type === 'signature_delta') {
    return { block_type: 'ThinkingBlock', thinking: '', signature: '' };
  }
  if (delta.type === 'input_json_delta') {
    return { block_type: 'ToolUseBlock', id: '', name: '', input: {} };
  }
  return { block_type: 'TextBlock', text: '', citations: null };
}

function _applyDelta(block: ContentBlock, delta: SseContentBlockDelta['delta']): ContentBlock {
  switch (delta.type) {
    case 'text_delta':
      if (block.block_type === 'TextBlock') {
        return { ...block, text: (block.text ?? '') + delta.text };
      }
      return block;
    case 'thinking_delta':
      if (block.block_type === 'ThinkingBlock') {
        return { ...block, thinking: (block.thinking ?? '') + delta.thinking };
      }
      return block;
    case 'signature_delta':
      if (block.block_type === 'ThinkingBlock') {
        return { ...block, signature: (block.signature ?? '') + delta.signature };
      }
      return block;
    case 'citations_delta':
      if (block.block_type === 'TextBlock') {
        const next: Citation[] = block.citations ? [...block.citations, delta.citation] : [delta.citation];
        return { ...block, citations: next };
      }
      return block;
    case 'input_json_delta':
      // Partial JSON accumulates in ``partialToolInput`` at the turn level;
      // the block.input mutation happens at content_block_stop.
      return block;
  }
}

function _applyDeltaToPartialTool(turn: Turn, event: SseContentBlockDelta): Pick<Turn, 'partialToolInput'> | undefined {
  if (event.delta.type !== 'input_json_delta') return undefined;
  const prev = turn.partialToolInput[event.index] ?? '';
  return {
    partialToolInput: {
      ...turn.partialToolInput,
      [event.index]: prev + event.delta.partial_json,
    },
  };
}

function _closeOpenThinkingTimings(turn: Turn, endedAt: number, excludeIndex?: number): Record<number, BlockTiming> {
  let blockTimings = turn.blockTimings;
  for (const index of turn.order) {
    if (index === excludeIndex) continue;
    const block = turn.blocks[index];
    if (block?.block_type !== 'ThinkingBlock') continue;
    blockTimings = _closeTimingAt(blockTimings, index, endedAt);
  }
  return blockTimings;
}

function _closeTimingAt(
  blockTimings: Record<number, BlockTiming>,
  index: number,
  endedAt: number
): Record<number, BlockTiming> {
  const timing = blockTimings[index];
  if (!timing || timing.endedAt !== undefined) return blockTimings;
  return { ...blockTimings, [index]: { ...timing, endedAt } };
}

function _onSseContentBlockStop(
  state: AgentPanelState,
  event: { type: 'content_block_stop'; index: number }
): AgentPanelState {
  // Capture the parsed tool-use block (if any) so we can route it
  // through the activity processor *after* the turn-level mutation.
  let stoppedBlock: ToolUseBlock | null = null;
  const stoppedAt = Date.now();
  const next = _updateActiveStreamTurn(state, (turn) => {
    if (turn.committed) return turn;
    // Stamp the end time on whatever block this index refers to —
    // not just tool calls. Thinking blocks need this to render an
    // accurate duration; text blocks could too if we ever wanted
    // a "wrote N tokens in Xs" tail.
    const existingTiming = turn.blockTimings[event.index];
    const blockTimings = existingTiming
      ? { ...turn.blockTimings, [event.index]: { ...existingTiming, endedAt: stoppedAt } }
      : { ...turn.blockTimings, [event.index]: { startedAt: stoppedAt, endedAt: stoppedAt } };
    const partial = turn.partialToolInput[event.index];
    const nextPartial = { ...turn.partialToolInput };
    delete nextPartial[event.index];
    if (!partial) return { ...turn, blockTimings, partialToolInput: nextPartial };
    const block = turn.blocks[event.index];
    if (!block || (block.block_type !== 'ToolUseBlock' && block.block_type !== 'ServerToolUseBlock')) {
      return { ...turn, blockTimings, partialToolInput: nextPartial };
    }
    let parsed: Record<string, unknown>;
    try {
      parsed = JSON.parse(partial);
    } catch {
      // The stream declared this input block closed. Drop the partial
      // marker even if the SDK produced malformed JSON so the UI never
      // treats the tool input as open forever. The final assistant
      // frame can still repair the canonical input.
      return { ...turn, blockTimings, partialToolInput: nextPartial };
    }
    const updated: ToolUseBlock | ContentBlock = { ...block, input: parsed } as ToolUseBlock;
    stoppedBlock = updated as ToolUseBlock;
    const blocks = { ...turn.blocks, [event.index]: updated };
    return { ...turn, blocks, partialToolInput: nextPartial, blockTimings };
  });
  if (stoppedBlock) {
    return _processToolUseBlock(next, stoppedBlock);
  }
  return next;
}

function _onSseMessageDelta(state: AgentPanelState, event: SseMessageDelta): AgentPanelState {
  return _updateActiveStreamTurn(state, (turn) => {
    if (turn.committed) return turn;
    return {
      ...turn,
      stopReason: event.delta?.stop_reason ?? turn.stopReason,
      // ``message_delta.usage`` from Anthropic only carries the
      // cumulative ``output_tokens`` for the in-flight message —
      // ``input_tokens`` / ``cache_creation_input_tokens`` /
      // ``cache_read_input_tokens`` are NOT in this event. Replacing
      // ``turn.usage`` outright would wipe the input + cache fields
      // that ``message_start`` supplied, which is exactly what made
      // the context-window chip swing wildly between turns. Merge
      // instead so output deltas layer on top of the original input
      // breakdown.
      usage: _mergeUsage(turn.usage, event.usage),
    };
  });
}

/**
 * Layer ``next`` over ``prev`` field-by-field, defined values winning.
 *
 * We merge rather than spread because spread (``{...prev, ...next}``)
 * lets a ``next`` whose fields are explicit ``undefined`` clobber
 * ``prev``'s set values. Anthropic's stream events emit partial usage
 * objects (e.g. ``message_delta`` with only ``output_tokens``); a
 * spread would null out every other field. This helper preserves
 * the prior value whenever ``next`` doesn't supply one.
 */
function _mergeUsage(prev: SseUsage | undefined, next: SseUsage | undefined): SseUsage | undefined {
  if (!next) return prev;
  if (!prev) return next;
  const merged: SseUsage = { ...prev };
  for (const [key, value] of Object.entries(next) as [keyof SseUsage, unknown][]) {
    if (value !== undefined) (merged as Record<string, unknown>)[key as string] = value;
  }
  return merged;
}

function _onSseMessageStop(state: AgentPanelState): AgentPanelState {
  // ``message_stop`` is the Anthropic SSE end-of-message marker — it
  // arrives once per assistant turn, *after* every per-block
  // ``assistant`` frame the SDK emits. Use it as the commit signal so
  // every text_delta / thinking_delta in a multi-block reply (e.g.
  // thinking + text) lands on the streaming turn before we freeze it.
  // Committing earlier (on the first intermediate assistant frame)
  // would null out ``activeStreamTurnId`` and silently drop the rest
  // of the deltas — see ``_onAssistant``'s ``isLiveStreaming`` guard.
  if (state.activeStreamTurnId === null) return state;
  return _commitStreamingTurn(state);
}

/**
 * Mark the active streaming turn ``committed`` and clear the pointer.
 *
 * Mirrors the awaitingAssistant logic in ``_onAssistant`` — when the
 * turn that just closed was a tool_use (signaled via the streaming
 * ``stopReason`` captured from ``message_delta``), keep the bouncing
 * dots on so the user sees the agent is still working while the tool
 * runs.
 */
function _commitStreamingTurn(state: AgentPanelState): AgentPanelState {
  const turnId = state.activeStreamTurnId;
  if (turnId === null) return state;
  const idx = state.turns.findIndex((turn) => turn.id === turnId || turn.messageId === turnId);
  if (idx < 0) return { ...state, activeStreamTurnId: null };
  const existing = state.turns[idx];
  if (existing.role !== 'assistant' || existing.committed) {
    return { ...state, activeStreamTurnId: null };
  }
  const committedAt = Date.now();
  const turns = state.turns.slice();
  turns[idx] = {
    ...existing,
    committed: true,
    blockTimings: _closeOpenThinkingTimings(existing, committedAt),
  };
  const visibleContent = existing.order
    .map((blockIdx) => existing.blocks[blockIdx])
    .filter((block): block is ContentBlock => block !== undefined);
  const stoppedForTool = _assistantFrameAwaitsTool(visibleContent, existing.stopReason);
  const next = {
    ...state,
    turns,
    activeStreamTurnId: null,
    awaitingAssistant: stoppedForTool,
  };
  return _completeTurnWorkAfterTerminalAssistant(next, existing.stopReason);
}

// =============================================================================
// Result / subagents
// =============================================================================

function _onResult(state: AgentPanelState, frame: ResultFrame): AgentPanelState {
  const cost = frame.total_cost_usd ?? 0;
  const keepsTurnActive = _resultKeepsTurnActive(state, frame);
  // Safety net for missing ``message_stop``: if a terminal result
  // frame (errored, interrupted, or an SDK that elided ``message_stop``)
  // arrives while a streaming turn is still in-flight, commit it now
  // so the UI doesn't render a forever-shimmering bubble. Normal flow
  // already committed at ``message_stop``; this fires only on the
  // fallback path. ``awaitingAssistant`` is reset below per the
  // result's ``keepsTurnActive`` — the result frame is the
  // authoritative end-of-turn signal and overrides whatever the
  // commit-fallback latched.
  const baseState = !keepsTurnActive && state.activeStreamTurnId !== null ? _commitStreamingTurn(state) : state;
  const hasSubmittedUser = !keepsTurnActive && _submittedUserAwaitsAssistant(baseState);
  const remainsActive = keepsTurnActive || hasSubmittedUser;
  // A turn ending — successful or failed — supersedes any prior
  // transient errors. If this frame *itself* reports an error the
  // server will have already pushed a ``server_error`` frame after it,
  // which repopulates ``errors`` for the next render.
  // If we were suppressing busy state for a pending interrupt,
  // honor the user's stop intent at the result seam too: the SDK
  // can emit a result frame in the middle of a multi-iteration turn
  // (``remainsActive = true``) and ``_submittedUserAwaitsAssistant``
  // would otherwise re-flip ``awaitingAssistant`` true, bringing
  // back the working indicator the user thought they had stopped.
  const wasInterruptPending = isInterruptPending(baseState);
  return {
    ...baseState,
    lastResult: frame,
    turnWork: _turnWorkForResult(baseState.turnWork, frame, remainsActive && !wasInterruptPending),
    cumulativeCostUsd: baseState.cumulativeCostUsd + cost,
    awaitingAssistant: remainsActive && !wasInterruptPending,
    activeStreamTurnId: keepsTurnActive && !wasInterruptPending ? baseState.activeStreamTurnId : null,
    errors: frame.is_error ? baseState.errors : [],
    rateLimit: baseState.rateLimit?.status === 'rejected' && !frame.is_error ? undefined : baseState.rateLimit,
    // Result frames are the server's authoritative end-of-turn signal,
    // including for interrupts. Clear here regardless of stop_reason
    // so subsequent turns (including ones the user starts immediately
    // after an interrupt completes) derive busy state normally again.
    interruptPendingSince: null,
  };
}

function _submittedUserAwaitsAssistant(state: AgentPanelState): boolean {
  const last = state.turns[state.turns.length - 1];
  if (
    last?.role === 'user' &&
    _turnAwaitsAssistant(last) &&
    state.turns.slice(0, -1).some((turn) => turn.role === 'assistant')
  ) {
    return true;
  }
  return state.turns.some(
    (turn, index) =>
      turn.role === 'user' &&
      turn.optimistic === true &&
      _turnAwaitsAssistant(turn) &&
      state.turns.slice(0, index).some((prior) => prior.role === 'assistant')
  );
}

function _turnWorkForResult(
  turnWork: AgentTurnWorkState,
  frame: ResultFrame,
  keepsTurnActive: boolean
): AgentTurnWorkState {
  if (keepsTurnActive) {
    return { ...turnWork, completedDurationMs: null };
  }

  const durationMs =
    _validDurationMs(frame.last_completed_turn_duration_ms) ??
    (turnWork.startedAt !== null ? Math.max(0, Date.now() - turnWork.startedAt) : null);
  return {
    startedAt: null,
    completedDurationMs: durationMs ?? turnWork.completedDurationMs,
  };
}

function _completeTurnWorkAfterTerminalAssistant(
  state: AgentPanelState,
  stopReason: StopReason | string | null | undefined
): AgentPanelState {
  if (!_assistantStopReasonCompletesTurn(stopReason)) return state;
  if (state.awaitingAssistant || state.activeStreamTurnId !== null) return state;
  if (_submittedUserAwaitsAssistant(state)) return state;
  const turnWork = _completeTurnWorkFromLocalElapsed(state.turnWork);
  return turnWork === state.turnWork ? state : { ...state, turnWork };
}

function _assistantStopReasonCompletesTurn(stopReason: StopReason | string | null | undefined): boolean {
  return stopReason !== null && stopReason !== undefined && stopReason !== 'tool_use';
}

function _completeTurnWorkFromLocalElapsed(turnWork: AgentTurnWorkState): AgentTurnWorkState {
  if (turnWork.startedAt === null) return turnWork;
  return {
    startedAt: null,
    completedDurationMs: Math.max(0, Date.now() - turnWork.startedAt),
  };
}

function _validDurationMs(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : null;
}

function _resultKeepsTurnActive(state: AgentPanelState, frame: ResultFrame): boolean {
  if (frame.is_error || frame.subtype === 'error') return false;

  const last = state.turns[state.turns.length - 1];
  if (last?.role !== 'assistant' || last.stopReason !== 'tool_use') return false;

  return last.order.some((index) => {
    const block = last.blocks[index];
    if (block?.block_type !== 'ToolUseBlock' && block?.block_type !== 'ServerToolUseBlock') return false;
    const tool = block as ToolUseBlock;
    return !_toolUseRunsInBackground(tool) && state.toolResults[tool.id] !== undefined;
  });
}

function _toolUseRunsInBackground(tool: ToolUseBlock): boolean {
  return tool.input?.run_in_background === true;
}

/**
 * Reset session-attached state — used by the provider when a new
 * WebSocket is being opened (or the existing one is being torn down)
 * so the status indicator doesn't show a stale "Ready" from the
 * previous session before the next ``session_attached`` frame
 * arrives, AND so the bouncing-dot "Thinking…" placeholder doesn't
 * stick around forever after the user clicks away mid-stream and
 * back into the same session.
 *
 * Visible turn list is preserved so re-attaching to the same session
 * feels continuous. Callers that are only reconnecting the browser
 * socket can preserve running tasks; callers that respawn the SDK
 * subprocess (effort changes) must clear them so stale task ids don't
 * leave the activity badges stuck on a phantom "1 shell running"
 * forever.
 */
export function resetAttached(
  state: AgentPanelState,
  options: { preserveCompletedTurnWork?: boolean; preserveActiveTasks?: boolean } = {}
): AgentPanelState {
  if (
    !state.attached &&
    !state.awaitingAssistant &&
    state.activeStreamTurnId === null &&
    Object.keys(state.activity.activeTasks).length === 0 &&
    Object.keys(state.subagents).length === 0
  ) {
    return state;
  }
  // Socket close mid-stream needs the same commit safety net as
  // ``_onServerError`` and ``_onResult``: without it, ``turn.committed``
  // stays ``false``, ``AssistantStream`` keeps the trailing thinking
  // block live, and the "Thinking for Ns" counter ticks forever on the
  // orphaned turn after the session reattaches.
  const committed = state.activeStreamTurnId !== null ? _commitStreamingTurn(state) : state;
  const preserveActiveTasks = options.preserveActiveTasks === true;
  const next: AgentPanelState = {
    ...committed,
    attached: false,
    awaitingAssistant: false,
    activeStreamTurnId: null,
    turnWork:
      options.preserveCompletedTurnWork && committed.turnWork.completedDurationMs !== null
        ? { startedAt: null, completedDurationMs: committed.turnWork.completedDurationMs }
        : emptyTurnWork(),
    subagents: preserveActiveTasks ? committed.subagents : {},
    activity: preserveActiveTasks
      ? committed.activity
      : _withRecountedActivity({ ...committed.activity, activeTasks: {} }),
    // Reconnect resets transient busy-state suppression. The new
    // session starts clean; any pending interrupt belonged to the
    // previous attachment and is no longer relevant.
    interruptPendingSince: null,
  };
  return { ...next, status: _deriveStatus(next) };
}

/**
 * Drop the loaded transcript window before a clean refetch.
 *
 * Used when the ring replay reports ``replayed=false`` for a reconnect
 * that observed live frames before the drop. The runtime is about to
 * call ``_maybeFetchHistory`` again to repopulate from REST; clearing
 * the window first guarantees the next ``prependHistory`` sees a clean
 * slate and there is no overlap between stale post-attach live frames
 * and the fresh REST tail. ``activity`` and ``toolResults`` are
 * preserved so the chip rail and tool inlining survive the reset.
 */
export function resetTurns(state: AgentPanelState): AgentPanelState {
  if (state.turns.length === 0 && state.activeStreamTurnId === null && state.pendingAnchoredErrors.length === 0) {
    return state;
  }
  const next: AgentPanelState = {
    ...state,
    turns: [],
    activeStreamTurnId: null,
    pendingAnchoredErrors: [],
  };
  return { ...next, status: _deriveStatus(next) };
}

/**
 * Optimistically swap the displayed effort. The provider calls this
 * the moment the user picks a new level so the input footer's pill
 * flips immediately, without waiting for the WS reconnect +
 * ``session_attached`` round-trip to land. The next ``session_attached``
 * is the source of truth and will overwrite this if the server
 * disagrees (e.g. validation rejection).
 */
export function applyOptimisticEffort(state: AgentPanelState, effort: string): AgentPanelState {
  if (state.effort === effort) return state;
  return { ...state, effort };
}

export function applyOptimisticSpeed(state: AgentPanelState, speed: AgentSpeed): AgentPanelState {
  if (state.speed === speed) return state;
  return { ...state, speed };
}

export function applyOptimisticPermissionMode(state: AgentPanelState, mode: PermissionMode): AgentPanelState {
  if (state.permissionMode === mode) return state;
  return { ...state, permissionMode: mode };
}

export function applyTaskStopped(state: AgentPanelState, taskId: string): AgentPanelState {
  const task = state.activity.activeTasks[taskId];
  const next = _dropActiveTask(state, taskId);
  return task?.toolUseId ? applyForegroundToolStopped(next, task.toolUseId) : next;
}

function applyForegroundToolStopped(state: AgentPanelState, toolUseId: string): AgentPanelState {
  if (!toolUseId) return state;

  const stoppedResult: ToolResultBlock = {
    block_type: 'ToolResultBlock',
    tool_use_id: toolUseId,
    content: 'Stopped by user',
    is_error: true,
  };

  let changed = false;
  const turns = state.turns.map((turn) => {
    if (turn.role !== 'assistant' || !turnHasToolUse(turn, toolUseId)) return turn;
    changed = true;
    return {
      ...turn,
      committed: true,
      partialToolInput: {},
    };
  });

  if (!changed && state.toolResults[toolUseId] !== undefined) return state;

  const next: AgentPanelState = {
    ...state,
    turns,
    awaitingAssistant: false,
    toolResults: state.toolResults[toolUseId]
      ? state.toolResults
      : {
          ...state.toolResults,
          [toolUseId]: stoppedResult,
        },
  };
  return { ...next, status: _deriveStatus(next) };
}

function _onTaskStarted(state: AgentPanelState, frame: TaskStartedFrame): AgentPanelState {
  const toolName = frame.tool_use_id ? (state.toolUseRegistry[frame.tool_use_id] ?? null) : null;
  const category = _categorizeTask(toolName, frame.task_type, frame.description);
  const activeTask: ActiveTask = {
    taskId: frame.task_id,
    toolUseId: frame.tool_use_id ?? null,
    toolName,
    taskType: frame.task_type,
    description: frame.description,
    category,
    startedAt: Date.now(),
  };
  return {
    ...state,
    subagents: {
      ...state.subagents,
      [frame.task_id]: {
        taskId: frame.task_id,
        toolUseId: frame.tool_use_id ?? null,
        description: frame.description,
        taskType: frame.task_type,
        status: 'running',
      },
    },
    activity: _withRecountedActivity({
      ...state.activity,
      activeTasks: { ...state.activity.activeTasks, [frame.task_id]: activeTask },
    }),
  };
}

function _onTaskProgress(state: AgentPanelState, frame: TaskProgressFrame): AgentPanelState {
  const prev = state.subagents[frame.task_id];
  let next = state;
  if (prev) {
    next = {
      ...next,
      subagents: {
        ...next.subagents,
        [frame.task_id]: {
          ...prev,
          toolUseId: frame.tool_use_id ?? prev.toolUseId,
          description: frame.description,
          lastToolName: frame.last_tool_name ?? prev.lastToolName,
          usage: frame.usage ?? prev.usage,
        },
      },
    };
  }
  const activePrev = next.activity.activeTasks[frame.task_id];
  if (activePrev) {
    next = {
      ...next,
      activity: {
        ...next.activity,
        activeTasks: {
          ...next.activity.activeTasks,
          [frame.task_id]: {
            ...activePrev,
            toolUseId: frame.tool_use_id ?? activePrev.toolUseId,
            description: frame.description,
            lastToolName: frame.last_tool_name ?? activePrev.lastToolName,
            usage: frame.usage ?? activePrev.usage,
          },
        },
      },
    };
  }
  return next;
}

function _onTaskNotification(state: AgentPanelState, frame: TaskNotificationFrame): AgentPanelState {
  // Non-terminal notifications (Monitor match events) carry progress,
  // not completion — the watch pipeline keeps running after a match.
  // Treating them as terminal was the "monitor chip appears then
  // instantly vanishes" bug: the first matched line evicted the chip.
  if (!isTerminalTaskStatus(frame.status)) {
    return state;
  }
  const taskId = _taskIdForNotification(state, frame);
  const prev = taskId ? state.subagents[taskId] : undefined;
  let next = state;
  if (prev) {
    const status: SubagentState['status'] =
      frame.status === 'completed' || frame.status === 'failed' || frame.status === 'stopped'
        ? frame.status
        : 'stopped';
    next = {
      ...next,
      subagents: {
        ...next.subagents,
        [prev.taskId]: {
          ...prev,
          status,
          outputFile: frame.output_file,
          summary: frame.summary,
          usage: frame.usage ?? prev.usage,
        },
      },
    };
  }
  // Drop the task from the active set — the status is terminal.
  if (taskId && next.activity.activeTasks[taskId]) {
    const { [taskId]: _drop, ...rest } = next.activity.activeTasks;
    void _drop;
    next = {
      ...next,
      activity: _withRecountedActivity({ ...next.activity, activeTasks: rest }),
    };
  }
  if (next.awaitingAssistant && !hasUnresolvedCommittedTool(next)) {
    next = _clearAwaitingIfNoPendingTool(next);
  }
  return next;
}

function _taskIdForNotification(state: AgentPanelState, frame: TaskNotificationFrame): string | null {
  const direct = frame.task_id;
  if (direct && (state.activity.activeTasks[direct] || state.subagents[direct])) return direct;
  const toolUseId = frame.tool_use_id;
  if (toolUseId) {
    const active = Object.values(state.activity.activeTasks).find((task) => task.toolUseId === toolUseId);
    if (active) return active.taskId;
    const subagent = Object.values(state.subagents).find((task) => task.toolUseId === toolUseId);
    if (subagent) return subagent.taskId;
  }
  return direct ?? null;
}

function _dropActiveTask(state: AgentPanelState, taskId: string): AgentPanelState {
  if (!taskId) return state;
  const hasActiveTask = state.activity.activeTasks[taskId] !== undefined;
  const prevSubagent = state.subagents[taskId];
  if (!hasActiveTask && !prevSubagent) return state;

  let next = state;
  if (prevSubagent) {
    next = {
      ...next,
      subagents: {
        ...next.subagents,
        [taskId]: {
          ...prevSubagent,
          status: 'stopped',
        },
      },
    };
  }

  if (next.activity.activeTasks[taskId]) {
    const { [taskId]: _drop, ...rest } = next.activity.activeTasks;
    void _drop;
    next = {
      ...next,
      activity: _withRecountedActivity({ ...next.activity, activeTasks: rest }),
    };
  }

  if (next.awaitingAssistant && !hasUnresolvedCommittedTool(next)) {
    next = _clearAwaitingIfNoPendingTool(next);
  }
  return next;
}

function _clearAwaitingIfNoPendingTool(state: AgentPanelState): AgentPanelState {
  if (!state.awaitingAssistant) return state;
  if (hasUnresolvedCommittedTool(state)) return state;
  const next = { ...state, awaitingAssistant: false };
  return { ...next, status: _deriveStatus(next) };
}

// =============================================================================
// Activity helpers
// =============================================================================

function emptyActivity(): ActivityState {
  return {
    activeTasks: {},
    bashCount: 0,
    monitorCount: 0,
    agentCount: 0,
    plan: null,
  };
}

function emptyTurnWork(): AgentTurnWorkState {
  return {
    startedAt: null,
    completedDurationMs: null,
  };
}

function _categorizeTask(
  toolName: string | null,
  taskType: string | undefined,
  _description: string
): ActiveTaskCategory {
  if (toolName) {
    if (/^(bash|shell)$/i.test(toolName)) return 'bash';
    if (/^monitor$/i.test(toolName)) return 'monitor';
    if (toolName === 'Task' || toolName === 'Agent') return 'agent';
  }
  if (taskType === 'local_agent' || taskType === 'remote_agent') return 'agent';
  // Shell task type names vary across agent runtimes. They cover both
  // backgrounded Shell/Bash and Monitor watches; without the registry
  // we cannot tell those apart and bias toward ``bash`` since it is
  // the common case and keeps the global shell chip visible.
  if (taskType && /^(local_)?(bash|shell|command)$/i.test(taskType)) return 'bash';
  return 'unknown';
}

function _activeTaskFromSnapshot(
  state: AgentPanelState,
  frame: TaskStartedFrame,
  existing: ActiveTask | undefined
): ActiveTask {
  const toolName = frame.tool_use_id ? (state.toolUseRegistry[frame.tool_use_id] ?? existing?.toolName ?? null) : null;
  const toolUseId = frame.tool_use_id ?? null;
  const category = _categorizeTask(toolName, frame.task_type, frame.description);
  if (
    existing &&
    existing.taskId === frame.task_id &&
    existing.toolUseId === toolUseId &&
    existing.toolName === toolName &&
    existing.taskType === frame.task_type &&
    existing.description === frame.description &&
    existing.category === category
  ) {
    return existing;
  }
  return {
    ...existing,
    taskId: frame.task_id,
    toolUseId,
    toolName,
    taskType: frame.task_type,
    description: frame.description,
    category,
    startedAt: existing?.startedAt ?? Date.now(),
  };
}

function _sameActiveTaskRefs(a: Record<string, ActiveTask>, b: Record<string, ActiveTask>): boolean {
  const aKeys = Object.keys(a);
  if (aKeys.length !== Object.keys(b).length) return false;
  return aKeys.every((key) => a[key] === b[key]);
}

function _withRecountedActivity(activity: ActivityState): ActivityState {
  let bash = 0;
  let monitor = 0;
  let agent = 0;
  for (const t of Object.values(activity.activeTasks)) {
    if (t.category === 'bash') bash++;
    else if (t.category === 'monitor') monitor++;
    else if (t.category === 'agent') agent++;
  }
  if (activity.bashCount === bash && activity.monitorCount === monitor && activity.agentCount === agent) {
    return activity;
  }
  return { ...activity, bashCount: bash, monitorCount: monitor, agentCount: agent };
}

function _registerToolUse(state: AgentPanelState, toolUseId: string, name: string): AgentPanelState {
  if (!toolUseId || !name) return state;
  if (state.toolUseRegistry[toolUseId] === name) return state;
  return {
    ...state,
    toolUseRegistry: { ...state.toolUseRegistry, [toolUseId]: name },
  };
}

/**
 * Funnel a tool_use block through the activity slice.
 *
 * Registers the ``id → name`` mapping, derives checklist state from
 * ``TodoWrite`` inputs, and
 * back-propagates the tool name to any active task that started
 * before the registry knew it (e.g. when the SDK ships the task_started
 * frame ahead of the streamed content_block_stop).
 */
function _processToolUseBlock(
  state: AgentPanelState,
  block: { id?: string; name?: string; input?: Record<string, unknown> }
): AgentPanelState {
  const id = block.id ?? '';
  const name = block.name ?? '';
  if (!id || !name) return state;
  let next = _registerToolUse(state, id, name);
  if (name === 'TodoWrite' && block.input) {
    // Match Claude Code's CLI behavior: a ``TodoWrite`` whose entire
    // checklist is ``status === 'completed'`` (or empty) clears the
    // active plan rather than leaving a stale "all done" badge in
    // the strip. The CLI itself does ``setTodos(all completed ? []
    // : todos)`` — see the bundled ``cli.js`` ``hY.call`` impl.
    const todosRaw = (block.input as Record<string, unknown>).todos;
    const allCleared = _isClearedTodoList(todosRaw);
    if (allCleared) {
      next = { ...next, activity: { ...next.activity, plan: null } };
    } else {
      const plan = _readPlanFromTodoWrite(id, block.input);
      if (plan) {
        next = { ...next, activity: { ...next.activity, plan } };
      }
    }
  } else if (name === 'TaskStop' && block.input) {
    const taskId = _stringInput(block.input, ['task_id']);
    if (taskId) next = _dropActiveTask(next, taskId);
  } else if ((name === 'KillShell' || name === 'KillBash') && block.input) {
    const taskId = _stringInput(block.input, ['shell_id', 'bash_id', 'task_id']);
    if (taskId) next = _dropActiveTask(next, taskId);
  }
  // Back-fill category for any active task that referenced this tool
  // use id before its name was known. ``local_bash`` tasks in
  // particular need this because category for backgrounded ``Bash`` vs
  // ``Monitor`` is indistinguishable without the registry.
  const tasksToReclass = Object.values(next.activity.activeTasks).filter(
    (t) => t.toolUseId === id && t.toolName !== name
  );
  if (tasksToReclass.length > 0) {
    const updatedTasks = { ...next.activity.activeTasks };
    for (const t of tasksToReclass) {
      updatedTasks[t.taskId] = {
        ...t,
        toolName: name,
        category: _categorizeTask(name, t.taskType, t.description),
      };
    }
    next = {
      ...next,
      activity: _withRecountedActivity({ ...next.activity, activeTasks: updatedTasks }),
    };
  }
  return next;
}

const TASK_LIST_TOOL_NAMES = new Set(['TaskCreate', 'TaskUpdate', 'TaskGet', 'TaskList']);

function _processToolResultBlock(state: AgentPanelState, result: ToolResultBlock): AgentPanelState {
  const withBackgroundTask = _processBackgroundTaskToolResult(state, result);
  const tool = _findToolUseBlock(withBackgroundTask, result.tool_use_id);
  if (!tool || !TASK_LIST_TOOL_NAMES.has(tool.name)) return withBackgroundTask;
  if (result.is_error === true) return withBackgroundTask;
  const payload = _taskResultPayload(result, tool.name);
  if (!payload) return withBackgroundTask;
  return _processTaskToolResult(withBackgroundTask, tool, payload);
}

/**
 * Normalize a Task-tool result into the ``{ task } / { tasks } /
 * { taskId }`` payload :func:`_processTaskToolResult` consumes — thin
 * wrapper over the shared :func:`normalizeTaskResultPayload` policy.
 */
function _taskResultPayload(result: ToolResultBlock, name: string): Record<string, unknown> | null {
  return normalizeTaskResultPayload(name, _toolResultPayload(result), _toolResultText(result.content));
}

function _processBackgroundTaskToolResult(state: AgentPanelState, result: ToolResultBlock): AgentPanelState {
  if (result.is_error === true) return state;
  const tool = _findToolUseBlock(state, result.tool_use_id);
  if (!tool || !/^(bash|shell|monitor)$/i.test(tool.name)) return state;
  const taskId = _backgroundTaskIdFromToolResult(result);
  if (!taskId) return state;

  const existing = state.activity.activeTasks[taskId] ?? getActiveTaskByToolUse(state, result.tool_use_id);
  const taskFrame: TaskStartedFrame = {
    type: 'task_started',
    task_id: taskId,
    tool_use_id: result.tool_use_id,
    description: _backgroundTaskDescription(tool),
    task_type: 'local_bash',
  };
  const activeTask = _activeTaskFromSnapshot(state, taskFrame, existing);
  if (existing === activeTask && state.activity.activeTasks[taskId] === activeTask) return state;

  const activeTasks: Record<string, ActiveTask> =
    existing && existing.taskId !== taskId
      ? Object.fromEntries(Object.entries(state.activity.activeTasks).filter(([id]) => id !== existing.taskId))
      : { ...state.activity.activeTasks };
  activeTasks[taskId] = activeTask;
  return {
    ...state,
    activity: _withRecountedActivity({ ...state.activity, activeTasks }),
  };
}

function _backgroundTaskIdFromToolResult(result: ToolResultBlock): string | null {
  const payload = _toolResultPayload(result);
  const structured = _backgroundTaskIdFromPayload(payload);
  if (structured) return structured;

  const text = _toolResultText(result.content);
  if (!text) return null;
  return _BACKGROUND_TASK_RESULT_RE.exec(text)?.groups?.taskId ?? null;
}

function _backgroundTaskIdFromPayload(payload: unknown): string | null {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return null;
  const record = payload as Record<string, unknown>;
  return (
    asString(record.taskId) ??
    asString(record.task_id) ??
    asString(record.shellId) ??
    asString(record.shell_id) ??
    asString(record.bashId) ??
    asString(record.bash_id)
  );
}

function _backgroundTaskDescription(tool: ToolUseBlock): string {
  return (
    _stringInput(tool.input, ['description', 'command']) ?? (tool.name === 'Monitor' ? 'Monitor' : 'Background task')
  );
}

function _findToolUseBlock(state: AgentPanelState, toolUseId: string): ToolUseBlock | null {
  if (!toolUseId) return null;
  for (let i = state.turns.length - 1; i >= 0; i--) {
    const turn = state.turns[i];
    if (turn.role !== 'assistant') continue;
    for (let j = turn.order.length - 1; j >= 0; j--) {
      const block = turn.blocks[turn.order[j]];
      if (block?.block_type === 'ToolUseBlock' && block.id === toolUseId) return block;
    }
  }
  return null;
}

function _processTaskToolResult(
  state: AgentPanelState,
  tool: ToolUseBlock,
  payload: Record<string, unknown>
): AgentPanelState {
  const current = state.activity.plan?.kind === 'task' ? state.activity.plan.todos : [];
  let tasks: TodoItem[] | null = null;

  if (tool.name === 'TaskCreate') {
    const task = _taskItemFromCreate(payload.task, tool.input);
    if (task) tasks = _upsertTaskItem(current, task);
  } else if (tool.name === 'TaskUpdate') {
    if (payload.success === false) return state;
    const taskId = asString(payload.taskId) ?? asString(tool.input.taskId);
    if (!taskId) return state;
    const status = asString(tool.input.status) ?? _statusFromTaskUpdatePayload(payload);
    if (status === 'deleted') {
      tasks = current.filter((task) => task.id !== taskId);
    } else {
      const task = _taskItemFromUpdate(current, taskId, tool.input, payload);
      if (task) tasks = _upsertTaskItem(current, task);
    }
  } else if (tool.name === 'TaskGet') {
    if (payload.task === null) return state;
    const task = _taskItemFromRecord(payload.task);
    if (task) tasks = _upsertTaskItem(current, task);
  } else if (tool.name === 'TaskList') {
    const rawTasks = Array.isArray(payload.tasks) ? (payload.tasks as Record<string, unknown>[]) : null;
    if (rawTasks) tasks = rawTasks.map(_taskItemFromRecord).filter((task): task is TodoItem => task !== null);
  }

  if (tasks === null) return state;
  const plan = _readPlanFromTaskState(tool.id, tasks);
  return {
    ...state,
    activity: {
      ...state.activity,
      plan: plan && !_isClearedPlan(plan) ? plan : null,
    },
  };
}

function _toolResultPayload(result: ToolResultBlock): unknown {
  const content = result.content;
  if (typeof content === 'string') return _parseJson(content);
  if (Array.isArray(content)) return _parseJson(_toolResultText(content));
  if (content && typeof content === 'object') return content;
  return null;
}

function _parseJson(text: string | null): unknown {
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

function _toolResultText(content: ToolResultBlock['content']): string | null {
  if (typeof content === 'string') return content;
  if (!Array.isArray(content)) return null;
  const text = content
    .map((block) => {
      if (block.block_type === 'TextBlock') return block.text;
      const raw = block as unknown as { type?: string; text?: unknown };
      if (raw.type === 'text' && typeof raw.text === 'string') return raw.text;
      return '';
    })
    .filter(Boolean)
    .join('\n')
    .trim();
  return text || null;
}

function _taskItemFromCreate(rawTask: unknown, input: Record<string, unknown>): TodoItem | null {
  const task =
    rawTask && typeof rawTask === 'object' && !Array.isArray(rawTask) ? (rawTask as Record<string, unknown>) : {};
  const id = asString(task.id);
  const subject = asString(task.subject) ?? asString(input.subject);
  if (!id || !subject) return null;
  return _taskItemFromRecord({
    ...input,
    ...task,
    id,
    subject,
    status: asString(task.status) ?? asString(input.status) ?? 'pending',
  });
}

function _taskItemFromUpdate(
  current: TodoItem[],
  taskId: string,
  input: Record<string, unknown>,
  payload: Record<string, unknown>
): TodoItem | null {
  const existing = current.find((task) => task.id === taskId);
  const status = asString(input.status) ?? _statusFromTaskUpdatePayload(payload);
  const next: TodoItem = {
    ...(existing ?? { id: taskId, content: '', status: 'pending' }),
    id: taskId,
  };
  const subject = asString(input.subject);
  const description = asString(input.description);
  const activeForm = asString(input.activeForm);
  const owner = asString(input.owner);
  if (subject) next.content = subject;
  if (description) next.description = description;
  if (activeForm) next.activeForm = activeForm;
  if (status) next.status = status;
  if (owner) next.owner = owner;
  const addBlocks = _stringList(input.addBlocks);
  if (addBlocks.length > 0) next.blocks = [...(next.blocks ?? []), ...addBlocks];
  const addBlockedBy = _stringList(input.addBlockedBy);
  if (addBlockedBy.length > 0) next.blockedBy = [...(next.blockedBy ?? []), ...addBlockedBy];
  return next.content ? next : null;
}

function _taskItemFromRecord(rawTask: unknown): TodoItem | null {
  if (!rawTask || typeof rawTask !== 'object' || Array.isArray(rawTask)) return null;
  const task = rawTask as Record<string, unknown>;
  const id = asString(task.id);
  const subject = asString(task.subject);
  if (!id || !subject) return null;
  const item: TodoItem = {
    id,
    content: subject,
    status: asString(task.status) ?? 'pending',
  };
  const description = asString(task.description);
  const activeForm = asString(task.activeForm);
  const owner = asString(task.owner);
  if (description) item.description = description;
  if (activeForm) item.activeForm = activeForm;
  if (owner) item.owner = owner;
  const blocks = _stringList(task.blocks);
  if (blocks.length > 0) item.blocks = blocks;
  const blockedBy = _stringList(task.blockedBy);
  if (blockedBy.length > 0) item.blockedBy = blockedBy;
  return item;
}

function _upsertTaskItem(tasks: TodoItem[], task: TodoItem): TodoItem[] {
  let replaced = false;
  const next = tasks.map((existing) => {
    if (existing.id !== task.id) return existing;
    replaced = true;
    return task;
  });
  if (!replaced) next.push(task);
  return next;
}

function _statusFromTaskUpdatePayload(payload: Record<string, unknown>): string | null {
  const statusChange = payload.statusChange;
  if (!statusChange || typeof statusChange !== 'object' || Array.isArray(statusChange)) return null;
  const record = statusChange as Record<string, unknown>;
  return asString(record.to) ?? asString(record.status) ?? asString(record.newStatus);
}

function _stringInput(input: Record<string, unknown>, keys: string[]): string | null {
  for (const key of keys) {
    const value = input[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return null;
}

function _stringList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === 'string' && item.length > 0);
}

function asString(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value : null;
}

/**
 * Detect a ``TodoWrite`` payload that should clear the badge — an
 * empty list, or a list where every entry is ``status === 'completed'``.
 * Matches the CLI's own ``setTodos(allCompleted ? [] : todos)`` rule.
 */
function _isClearedTodoList(todosRaw: unknown): boolean {
  if (!Array.isArray(todosRaw)) return false;
  if (todosRaw.length === 0) return true;
  return todosRaw.every((entry) => {
    if (!entry || typeof entry !== 'object') return false;
    return (entry as Record<string, unknown>).status === 'completed';
  });
}

function _readPlanFromTodoWrite(toolUseId: string, input: Record<string, unknown>): PlanState | null {
  const todosRaw = input.todos;
  if (!Array.isArray(todosRaw)) return null;
  const todos: TodoItem[] = todosRaw.map((entry) => {
    const obj = (entry ?? {}) as Record<string, unknown>;
    return {
      content: typeof obj.content === 'string' ? obj.content : '',
      status: typeof obj.status === 'string' ? obj.status : 'pending',
      activeForm: typeof obj.activeForm === 'string' ? obj.activeForm : undefined,
    };
  });
  return _buildPlanState(toolUseId, todos, 'todo');
}

function _readPlanFromTaskState(toolUseId: string, tasksRaw: Record<string, unknown>[] | TodoItem[]): PlanState | null {
  if (!Array.isArray(tasksRaw)) return null;
  const todos: TodoItem[] = tasksRaw
    .map((entry) => ('content' in entry ? (entry as TodoItem) : _taskItemFromRecord(entry)))
    .filter((task): task is TodoItem => task !== null && task.status !== 'deleted');
  return _buildPlanState(toolUseId, todos, 'task');
}

function _isClearedPlan(plan: PlanState): boolean {
  return plan.total === 0 || (plan.total > 0 && plan.completed === plan.total);
}

function _buildPlanState(toolUseId: string, todos: TodoItem[], kind: 'todo' | 'task'): PlanState {
  let pending = 0;
  let inProgress = 0;
  let completed = 0;
  let cancelled = 0;
  let current: TodoItem | null = null;
  for (const t of todos) {
    if (t.status === 'in_progress') {
      inProgress++;
      if (current === null) current = t;
    } else if (t.status === 'completed') {
      completed++;
    } else if (t.status === 'cancelled' || t.status === 'canceled') {
      cancelled++;
    } else {
      pending++;
    }
  }
  return {
    toolUseId,
    kind,
    todos,
    total: todos.length,
    pending,
    inProgress,
    completed,
    cancelled,
    current,
    updatedAt: Date.now(),
  };
}

// =============================================================================
// Helpers
// =============================================================================

function _blocksToIndexed(blocks: ContentBlock[]): { blocks: Record<number, ContentBlock>; order: number[] } {
  const byIndex: Record<number, ContentBlock> = {};
  const order: number[] = [];
  blocks.forEach((b, i) => {
    byIndex[i] = b;
    order.push(i);
  });
  return { blocks: byIndex, order };
}

function _mergeStreamingAssistantBlocks(
  existingTurn: Turn,
  indexed: { blocks: Record<number, ContentBlock>; order: number[] }
): { blocks: Record<number, ContentBlock>; order: number[] } {
  const blocks: Record<number, ContentBlock> = { ...existingTurn.blocks };
  const order = [...existingTurn.order];
  for (const index of order) {
    const partial = existingTurn.partialToolInput[index];
    const block = blocks[index];
    if (partial !== undefined && block) {
      blocks[index] = _finalizePartialToolInput(block, partial);
    }
  }
  for (const index of indexed.order) {
    const incoming = indexed.blocks[index];
    const existing = existingTurn.blocks[index];
    if (existing) {
      if (existing.block_type === 'ThinkingBlock' && incoming.block_type === 'TextBlock') {
        const nextIndex = nextFreeBlockIndex(order);
        blocks[nextIndex] = incoming;
        order.push(nextIndex);
        continue;
      }
      blocks[index] = _mergeSameSlotBlock(existing, incoming);
    } else {
      blocks[index] = incoming;
      if (!order.includes(index)) order.push(index);
    }
  }
  return { blocks, order };
}

function nextFreeBlockIndex(order: number[]): number {
  return order.length > 0 ? Math.max(...order) + 1 : 0;
}

function _mergeCommittedAssistantBlocks(
  existingTurn: Turn,
  indexed: { blocks: Record<number, ContentBlock>; order: number[] }
): { blocks: Record<number, ContentBlock>; order: number[] } {
  const blocks: Record<number, ContentBlock> = { ...existingTurn.blocks };
  const order = [...existingTurn.order];
  let nextIndex = order.length > 0 ? Math.max(...order) + 1 : 0;

  for (const index of indexed.order) {
    const incoming = indexed.blocks[index];
    const existingIndex = order.find((candidateIndex) => _blocksEquivalent(blocks[candidateIndex], incoming));
    if (existingIndex !== undefined) {
      blocks[existingIndex] = _richerEquivalentBlock(blocks[existingIndex], incoming);
      continue;
    }
    blocks[nextIndex] = incoming;
    order.push(nextIndex);
    nextIndex += 1;
  }

  return { blocks, order };
}

function _blocksEquivalent(a: ContentBlock, b: ContentBlock): boolean {
  if (a.block_type !== b.block_type) return false;
  switch (a.block_type) {
    case 'TextBlock':
      return a.text === (b as TextBlock).text && _citationsEquivalent(a.citations, (b as TextBlock).citations);
    case 'ThinkingBlock':
      return a.thinking === (b as ThinkingBlock).thinking && a.signature === (b as ThinkingBlock).signature;
    case 'ToolUseBlock': {
      const tool = b as ToolUseBlock;
      return a.id === tool.id && a.name === tool.name;
    }
    case 'ServerToolUseBlock': {
      const tool = b as ToolUseBlock;
      return a.id === tool.id && a.name === tool.name;
    }
    default:
      return false;
  }
}

function _richerEquivalentBlock(existing: ContentBlock, incoming: ContentBlock): ContentBlock {
  if (existing.block_type !== incoming.block_type) return existing;
  if (existing.block_type === 'ToolUseBlock' || existing.block_type === 'ServerToolUseBlock') {
    const existingTool = existing as ToolUseBlock;
    const incomingTool = incoming as ToolUseBlock;
    return _toolInputScore(incomingTool.input) > _toolInputScore(existingTool.input) ? incoming : existing;
  }
  return existing;
}

function _mergeSameSlotBlock(existing: ContentBlock, incoming: ContentBlock): ContentBlock {
  if (_blocksEquivalent(existing, incoming)) return _richerEquivalentBlock(existing, incoming);
  if (existing.block_type === incoming.block_type) {
    if (existing.block_type === 'ToolUseBlock' || existing.block_type === 'ServerToolUseBlock') {
      return _richerEquivalentBlock(existing, incoming);
    }
  }
  return incoming;
}

function _finalizePartialToolInput(block: ContentBlock, partialJson: string): ContentBlock {
  if (block.block_type !== 'ToolUseBlock' && block.block_type !== 'ServerToolUseBlock') return block;
  try {
    const parsed: unknown = JSON.parse(partialJson);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? { ...block, input: parsed as Record<string, unknown> }
      : block;
  } catch {
    return block;
  }
}

function _toolInputScore(input: Record<string, unknown> | undefined): number {
  if (!input) return 0;
  let score = 0;
  for (const [key, value] of Object.entries(input)) {
    if (value === undefined || value === null) continue;
    score += 1;
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed) score += Math.min(1000, trimmed.length);
      if (key === 'description' || key === 'command' || key === 'cmd') score += 100;
    } else if (Array.isArray(value)) {
      score += value.length;
    } else if (typeof value === 'object') {
      score += Object.keys(value).length;
    } else {
      score += 1;
    }
  }
  return score;
}

function _citationsEquivalent(a: Citation[] | null | undefined, b: Citation[] | null | undefined): boolean {
  if (a === b) return true;
  if (!a && !b) return true;
  if (!a || !b || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (JSON.stringify(a[i]) !== JSON.stringify(b[i])) return false;
  }
  return true;
}

function _updateActiveStreamTurn(state: AgentPanelState, fn: (turn: Turn) => Turn): AgentPanelState {
  if (!state.activeStreamTurnId) return state;
  const idx = state.turns.findIndex(
    (turn) => turn.id === state.activeStreamTurnId || turn.messageId === state.activeStreamTurnId
  );
  if (idx < 0) return state;
  const current = state.turns[idx];
  if (current.role !== 'assistant' || current.committed) return state;
  const next = fn(current);
  if (next === current) return state;
  const turns = state.turns.slice();
  turns[idx] = next;
  return { ...state, turns };
}

function _deriveStatus(state: AgentPanelState): AgentStatus {
  if (!state.attached) return 'idle';
  // Suppress busy status while an interrupt is pending. In-flight
  // SDK frames the server emitted before processing the interrupt
  // (``message_start``, deltas, ``user`` echoes) still update the
  // turn store so partial content remains visible, but they must
  // not flip the status back to ``thinking`` / ``streaming`` /
  // ``tool_running`` -- otherwise the Stop button reappears the
  // instant the user clicked it and they have to click twice.
  if (isInterruptPending(state)) return 'idle';
  const last = state.turns[state.turns.length - 1];
  const activeStreamTurn = state.activeStreamTurnId
    ? (state.turns.find(
        (turn) => turn.id === state.activeStreamTurnId || turn.messageId === state.activeStreamTurnId
      ) ?? null)
    : null;
  const liveAssistant = activeStreamTurn?.role === 'assistant' && !activeStreamTurn.committed ? activeStreamTurn : last;
  if (liveAssistant && !liveAssistant.committed && liveAssistant.role === 'assistant') {
    const blocks = liveAssistant.order.map((i) => liveAssistant.blocks[i]);
    const hasOpenToolUse = blocks.some(
      (b) =>
        b?.block_type === 'ToolUseBlock' &&
        (Object.keys((b as ToolUseBlock).input ?? {}).length === 0 ||
          liveAssistant.partialToolInput[_indexOfBlock(liveAssistant, b)] !== undefined)
    );
    if (hasOpenToolUse) return 'tool_running';
    const hasOpenThinking = blocks.some((b) => b?.block_type === 'ThinkingBlock' && !(b as ThinkingBlock).signature);
    if (hasOpenThinking) return 'thinking';
    return 'streaming';
  }
  // No in-flight assistant turn — but if the user just sent a message
  // we want the footer chip + bouncing dots to reflect "thinking" right
  // away (before ``message_start`` arrives), not the misleading "Ready".
  if (state.awaitingAssistant) {
    // If we're awaiting because the just-committed turn fired a tool
    // that hasn't returned yet, that gap is ``tool_running`` — the
    // tool row's own spinner is the working signal, so flagging
    // ``thinking`` here on top would double up and read as "the
    // model is ruminating" while a script is just blocking on I/O.
    if (last?.committed && last.role === 'assistant') {
      const hasUnresolvedTool = last.order.some((i) => {
        const b = last.blocks[i];
        if (b?.block_type !== 'ToolUseBlock' && b?.block_type !== 'ServerToolUseBlock') return false;
        return isToolUsePending(state, (b as ToolUseBlock).id);
      });
      if (hasUnresolvedTool) return getForegroundTool(state) !== null ? 'tool_running' : 'idle';
    }
    return 'thinking';
  }
  return 'idle';
}

function _indexOfBlock(turn: Turn, block: ContentBlock): number {
  for (const i of turn.order) {
    if (turn.blocks[i] === block) return i;
  }
  return -1;
}

// =============================================================================
// Rendering helpers — pure, cheap to call per render.
// =============================================================================

/** Return a turn's blocks in emission order as a plain array. */
export function turnBlocks(turn: Turn): ContentBlock[] {
  return turn.order.map((i) => turn.blocks[i]).filter(Boolean);
}

/** Find a live subagent task spawned by a given Task tool_use block. */
export function getSubagentByToolUse(state: AgentPanelState, toolUseId: string): SubagentState | undefined {
  for (const s of Object.values(state.subagents)) {
    if (s.toolUseId === toolUseId) return s;
  }
  return undefined;
}

/** Find a live SDK task spawned by a given tool_use block. */
export function getActiveTaskByToolUse(state: AgentPanelState, toolUseId: string): ActiveTask | undefined {
  return Object.values(state.activity.activeTasks).find((task) => task.toolUseId === toolUseId);
}

/**
 * Synthesize :type:`ActiveTask` entries for in-flight ``Monitor``
 * tool_uses while they are still waiting on a result.
 *
 * Claude Code's ``Monitor`` tool is a long-running watcher that
 * fires-and-forgets: the agent often issues the call, gets back a
 * "started, id=X" tool_result, and moves on to other work while
 * the watcher continues firing in the background. The SDK's
 * ``task_started`` envelope is supposed to mirror that, but in
 * practice it lands inconsistently for ``Monitor`` (some Claude
 * Code versions skip it entirely, others tag it as the generic
 * ``local_bash`` type which our categorizer biases to ``bash``
 * when the tool registry hasn't caught up yet). The chip strip
 * and the "working..." indicator both pivot on
 * ``activity.monitorCount``, so a missing ``task_started`` made
 * monitors silently disappear from the UI even while the agent
 * was still watching.
 *
 * ``_processBackgroundTaskToolResult`` promotes the "started,
 * id=X" result into a real active task, which means the chip can
 * stop it by SDK task id. This selector only closes the earlier gap
 * between the streamed ``Monitor`` tool_use and either that
 * background-start result or a real ``task_started`` side-channel.
 * The synthetic ``taskId`` is namespaced
 * (``synth-monitor:<tool_use_id>``) so it can't collide with a real
 * SDK task id, and the synthesized entry is harmless if a real task
 * lands later (the dedup against :func:`getActiveTaskByToolUse`
 * skips it from that point).
 */
export function pendingMonitors(state: AgentPanelState): ActiveTask[] {
  const out: ActiveTask[] = [];
  for (const turn of state.turns) {
    if (turn.role !== 'assistant') continue;
    for (const i of turn.order) {
      const block = turn.blocks[i];
      if (block?.block_type !== 'ToolUseBlock' && block?.block_type !== 'ServerToolUseBlock') continue;
      const toolUse = block as ToolUseBlock;
      if (!/^monitor$/i.test(toolUse.name)) continue;
      // Defer the live-ness check to :func:`isToolUsePending` --
      // its semantics (no tool_result, owning assistant turn live
      // or still awaiting) match what the chip / working
      // indicator should treat as a meaningful monitor. This also
      // means a Monitor the agent acknowledged as timed out
      // (final ``end_turn`` assistant frame, no tool_result)
      // correctly drops out of the synthesized list.
      if (!isToolUsePending(state, toolUse.id)) continue;
      // Skip already-counted entries (real SDK task exists for
      // this tool_use). The merged chip list stays disjoint.
      if (getActiveTaskByToolUse(state, toolUse.id)) continue;
      out.push({
        taskId: `synth-monitor:${toolUse.id}`,
        toolUseId: toolUse.id,
        toolName: toolUse.name,
        // Tag the inferred entries so callers (chip popover,
        // debug) can tell them apart from real SDK tasks if they
        // ever care.
        taskType: 'inferred_from_tool_use',
        description: _describeMonitorInput(toolUse) ?? 'Monitor',
        category: 'monitor',
        startedAt: turn.blockTimings[i]?.startedAt ?? turn.startedAt,
      });
    }
  }
  return out;
}

/** True when at least one ``Monitor`` is live (real task or pending tool_use). */
export function hasActiveMonitor(state: AgentPanelState): boolean {
  if (state.activity.monitorCount > 0) return true;
  return pendingMonitors(state).length > 0;
}

function _describeMonitorInput(block: ToolUseBlock): string | null {
  const input = block.input;
  if (!input || typeof input !== 'object') return null;
  const candidates = ['description', 'command', 'target', 'pattern', 'name'];
  for (const key of candidates) {
    const value = (input as Record<string, unknown>)[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return null;
}

/** Find the foreground tool currently blocking the assistant turn, if any. */
export function getForegroundTool(state: AgentPanelState): ForegroundTool | null {
  // Use the most recent assistant turn rather than the literal tail.
  // A follow-up user message sent while a tool is still running lands
  // at the tail of ``state.turns`` and would otherwise mask the live
  // foreground tool — sending a message does not interrupt the call.
  const assistantTurn = _latestAssistantTurn(state);
  if (!assistantTurn) return null;

  for (const i of assistantTurn.order) {
    const block = assistantTurn.blocks[i];
    if (block?.block_type !== 'ToolUseBlock' && block?.block_type !== 'ServerToolUseBlock') continue;
    const tool = block as ToolUseBlock;
    if (!isToolUsePending(state, tool.id)) continue;
    if (getActiveTaskByToolUse(state, tool.id)) continue;

    return {
      toolUseId: tool.id,
      toolName: tool.name,
      description: _describeToolUse(tool),
      category: _categorizeTask(tool.name, undefined, ''),
      startedAt: assistantTurn.blockTimings[i]?.startedAt ?? assistantTurn.startedAt,
    };
  }

  return null;
}

/**
 * True when a tool row should present as actively pending.
 *
 * Missing tool_result alone is not enough: resumed / errored
 * transcripts can legitimately end with a tool_use whose result
 * never arrives, and showing that as an infinite spinner is worse
 * than admitting the run is no longer live. Treat a row as pending
 * only when there is an active task / subagent, an in-flight
 * streaming assistant block, or the current committed turn is still
 * awaiting its tool result.
 */
export function isToolUsePending(state: AgentPanelState, toolUseId: string): boolean {
  if (!toolUseId) return false;

  if (state.toolResults[toolUseId] !== undefined) return false;
  if (getActiveTaskByToolUse(state, toolUseId)) return true;
  const linkedSubagents = Object.values(state.subagents).filter((subagent) => subagent.toolUseId === toolUseId);
  if (linkedSubagents.some((subagent) => subagent.status === 'running')) {
    return true;
  }
  if (
    linkedSubagents.some(
      (subagent) => subagent.status === 'completed' || subagent.status === 'failed' || subagent.status === 'stopped'
    )
  ) {
    return false;
  }
  // Walk back to the assistant turn that actually emitted this tool_use
  // instead of relying on the literal tail. When the user sends a
  // follow-up message while a tool is still running, the optimistic
  // user turn lands at ``state.turns[-1]`` and the spinner would
  // otherwise vanish mid-execution even though nothing was interrupted.
  const owningTurn = _findOwningAssistantTurn(state, toolUseId);
  if (!owningTurn) return false;
  if (!owningTurn.committed) return true;
  return state.awaitingAssistant;
}

/**
 * True when the footer-level activity marker should render.
 *
 * This marker is intentionally separate from transcript rows: it says
 * "the agent is still working" while events replace/append normally.
 */
export function shouldShowWorkingIndicator(state: AgentPanelState): boolean {
  if (state.pendingInteractions.length > 0) return false;
  return (
    state.turnWork.startedAt !== null ||
    state.awaitingAssistant ||
    state.status === 'thinking' ||
    state.status === 'streaming' ||
    state.status === 'tool_running' ||
    // A live ``Monitor`` keeps the agent meaningfully busy --
    // the watcher is still firing tool callbacks even if the
    // assistant turn returned. Count it both via the canonical
    // ``activity.monitorCount`` (real SDK ``task_started``
    // frames) and via the :func:`pendingMonitors` fallback
    // selector that synthesizes entries when the SDK skipped
    // the side-channel envelope -- see that function's
    // docstring for why.
    hasActiveMonitor(state) ||
    state.activity.agentCount > 0
  );
}

export function getWorkingIndicatorModel(state: AgentPanelState): WorkingIndicatorModel | null {
  const active = shouldShowWorkingIndicator(state);
  const durationMs = state.turnWork.completedDurationMs;
  if (!active && durationMs === null) return null;
  return { active, durationMs };
}

/**
 * True iff the most recent committed assistant turn issued a tool_use
 * that is still live according to :func:`isToolUsePending`. Drives
 * the chat-tail indicator's "running, click to stop" variant.
 */
export function hasUnresolvedCommittedTool(state: AgentPanelState): boolean {
  // Use the most recent assistant turn rather than ``state.turns[-1]``
  // so a follow-up user message sent mid-tool doesn't make the chat
  // tail forget that a tool is still in flight.
  const assistantTurn = _latestAssistantTurn(state);
  if (!assistantTurn?.committed) return false;
  return assistantTurn.order.some((i) => {
    const b = assistantTurn.blocks[i];
    if (b?.block_type !== 'ToolUseBlock' && b?.block_type !== 'ServerToolUseBlock') return false;
    return isToolUsePending(state, (b as ToolUseBlock).id);
  });
}

function turnHasToolUse(turn: Turn, toolUseId: string): boolean {
  return turn.order.some((i) => {
    const b = turn.blocks[i];
    if (b?.block_type !== 'ToolUseBlock' && b?.block_type !== 'ServerToolUseBlock') return false;
    return (b as ToolUseBlock).id === toolUseId;
  });
}

function _latestAssistantTurn(state: AgentPanelState): Turn | null {
  for (let i = state.turns.length - 1; i >= 0; i--) {
    const turn = state.turns[i];
    if (turn.role === 'assistant') return turn;
  }
  return null;
}

function _findOwningAssistantTurn(state: AgentPanelState, toolUseId: string): Turn | null {
  for (let i = state.turns.length - 1; i >= 0; i--) {
    const turn = state.turns[i];
    if (turn.role !== 'assistant') continue;
    if (turnHasToolUse(turn, toolUseId)) return turn;
  }
  return null;
}

function _turnAwaitsAssistant(turn: Turn): boolean {
  return turn.awaitsAssistant !== false;
}

function _turnHasEquivalentBlock(turn: Turn, block: ContentBlock): boolean {
  return turn.order.some((index) => {
    const existing = turn.blocks[index];
    return existing !== undefined && _blocksEquivalent(existing, block);
  });
}

function _describeToolUse(tool: ToolUseBlock): string {
  const input = tool.input ?? {};
  for (const key of ['description', 'command', 'cmd', 'query', 'path']) {
    const value = input[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return tool.name;
}

// Re-export for convenience
export type { TextBlock, ThinkingBlock, ToolResultBlock, ToolUseBlock };
